import React, { useRef, useEffect, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthProvider';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import api from '../../../../api';
import Modal from '../../../../components/common/Modal';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import Alert from '../../../../components/Alert/Alert';
import { ArrowLeft, ArrowDownToLine, ClipboardCheck, Lock, UsersRound } from 'lucide-react';
import { FileText } from 'lucide-react';
import { GenericView, ActionButtons } from '../../../../components/common/table';
// import SendToClientButtonWithWorkflowModal from '../SendToClientButtonWithWorkflowModal';
import { sendProjectEmail, getProjectEmailTemplates, checkEmailSettings } from '../../api/projectEmailApi';
// Import file operations utilities
import { getFileIcon, formatFileSize, formatDate, transformFileData } from '../../../../utils/fileOperations';
import ExpenseForm from '../../../Expense/components/ExpenseForm';
import { getExpensesByProjectId, createExpense, updateExpense, deleteExpense } from '../../../Expense/api/expenseApi';
import { buildProjectAuditReadiness, isSustainabilityAuditProject } from '../EditProject/auditProcessUi';
import { getCurrencyPrefix } from '../../../../utils/currencyUtils';
import { buildAuditWorkflowProgress } from '../../../AuditProcess/auditWorkflowProgressUtils';


import { auditCycleStageRank } from '../../../AuditProcess/auditCycleUtils';

// AP-005: order proposal-item cycle rows (sub_services) by Audit Date ASC, with a
// canonical stage-rank tiebreaker so Initial never appears after a Surveillance /
// Re-certification when audit dates are equal or unset.
const sortCrmCycleRows = (rows = []) => {
  const toTs = (v) => {
    const t = String(v || '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(t) ? Date.parse(`${t}T00:00:00Z`) : Number.POSITIVE_INFINITY;
  };
  return [...rows]
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const dateDiff = toTs(a.row?.proposeDate) - toTs(b.row?.proposeDate);
      if (dateDiff) return dateDiff;
      const rankDiff = auditCycleStageRank({ serviceItem: a.row?.service_item, serviceItemName: a.row?.service_item })
        - auditCycleStageRank({ serviceItem: b.row?.service_item, serviceItemName: b.row?.service_item });
      if (rankDiff) return rankDiff;
      return a.i - b.i;
    })
    .map(({ row }) => row);
};

const getAuditAwarenessFromResponse = (responseData) => {
    const awareness = responseData?.orchestration?.auditHandlers || responseData?.auditDateAwareness || responseData?.audit_date_awareness;
    if (!awareness || !Array.isArray(awareness.handlers)) return null;
    const handlers = awareness.handlers
        .filter(Boolean)
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
    return {
        currentStep: awareness.currentStep || 'Review & Audit Scheduling',
        currentStepId: awareness.currentStepId || null,
        projectId: awareness.projectId || null,
        locked: Boolean(responseData?.orchestration?.locked || awareness.locked) || handlers.some((handler) => handler.taskId),
        lockReason: responseData?.orchestration?.lockReason || awareness.lockReason || '',
        handlers,
    };
};

const isCancelledAuditTaskStatus = (rawStatus) => {
    const status = String(rawStatus || '').trim().toUpperCase();
    return status === 'CANCELLED' || status === 'CANCELED';
};

const getAuditAwarenessFromTasks = (tasks = [], projectId = null) => {
    if (!Array.isArray(tasks) || tasks.length === 0) return null;
    const relevantTasks = tasks.filter((task) => !isCancelledAuditTaskStatus(task?.status));
    if (!relevantTasks.length) return null;

    const progress = buildAuditWorkflowProgress(relevantTasks);
    const activeStep = (progress.majorSteps || []).find((step) => step.id === progress.currentMajorId);
    if (!activeStep) return null;
    const taskDefs = activeStep.subSteps?.length
        ? activeStep.subSteps
        : [{ label: activeStep.label, codes: activeStep.completionCodes || [] }];
    const findTask = (codes) => relevantTasks.find((task) => new Set(codes || []).has(String(task?.taskTypeCode || '').trim().toUpperCase()));
    const toHandler = (task, fallbackStep, order) => ({
        order,
        step: task?.taskType || fallbackStep,
        taskId: task?.id,
        status: task?.status || 'OPEN',
        assigneeId: task?.assignedUserId || null,
        assigneeName: task?.assignedUsername || 'Assignee resolving',
        assigneeEmail: task?.assignedEmail || '',
    });
    const handlers = taskDefs.map((taskDef, idx) => toHandler(findTask(taskDef.codes), taskDef.label, idx + 1));

    return {
        currentStep: activeStep.label || 'Review & Audit Scheduling',
        currentStepId: activeStep.id || null,
        projectId,
        locked: true,
        lockReason: 'Audit Date changes are managed in Audit Scheduling.',
        handlers,
    };
};

const summarizeAuditHandlers = (awareness) => {
    const handlers = awareness?.handlers || [];
    if (awareness?.locked) return 'Audit Date is now managed in Audit Scheduling.';
    const named = handlers
        .map((handler) => handler.assigneeName || handler.assigneeEmail)
        .filter(Boolean);
    if (!named.length) return 'Audit Process tasks are ensured. Assignees are being resolved by competency.';
    return `Audit Process tasks are now visible: ${named.join(' and ')}.`;
};

const DetailProject = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { can } = useAuth();
    const canProject = (key) => can(key, 'project'); // This is lead ID from URL
    const [projectData, setProjectData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [realProjectId, setRealProjectId] = useState(null); // Store actual project ID

    const [activeTab, setActiveTab] = useState('tabs-project-item'); // Track active tab

    // Add state for dynamic investment breakdown
    const [investmentBreakdown, setInvestmentBreakdown] = useState(null);
    const [yearOptionsByServiceItem, setYearOptionsByServiceItem] = useState({});
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [loadingBreakdown, setLoadingBreakdown] = useState(false);
    const [proposalCurrencyCode, setProposalCurrencyCode] = useState('IDR');

    // Add state for project dates
    const [projectStartDate, setProjectStartDate] = useState('');
    const [projectEndDate, setProjectEndDate] = useState('');
    
    // Debug: Log state changes

    // Add state for project history
    const [projectHistory, setProjectHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Add state for file management
    const [files, setFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [alert, setAlert] = useState(null);

    // Add state for expense management
    const [expenses, setExpenses] = useState([]);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);

    // Add state for invoice management
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    const [companyId, setCompanyId] = useState(null); // Store company ID from project/proposal
    const [leadData, setLeadData] = useState(null); // Store lead data for project name

    // Inline Audit Date editing state
    const [editingProposeDateStandardId, setEditingProposeDateStandardId] = useState(null);
    const [editingProposeDateValue, setEditingProposeDateValue] = useState('');
    const [savingProposeDate, setSavingProposeDate] = useState(false);

    // Bulk Audit Date editing mode
    const [isEditingAuditDates, setIsEditingAuditDates] = useState(false);
    const [pendingAuditDates, setPendingAuditDates] = useState({});
    const auditDateInputRefs = useRef({});
    const [auditDateAwareness, setAuditDateAwareness] = useState(null);
    const isAuditDateLocked = Boolean(auditDateAwareness?.locked);

    // Document preview state
    const [documentPreview, setDocumentPreview] = useState(null);
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
    const [documentFile, setDocumentFile] = useState(null);
    const [uploadingDocument, setUploadingDocument] = useState(false);

    const handleAuditDateLockError = (err, fallbackMessage = 'Failed to update Audit Date') => {
        const responseData = err?.response?.data;
        const isLocked = err?.response?.status === 409 && responseData?.code === 'AUDIT_DATE_LOCKED_BY_AUDIT_PROCESS';
        if (!isLocked) return false;
        const awareness = getAuditAwarenessFromResponse(responseData);
        if (awareness) {
            setAuditDateAwareness({ ...awareness, locked: true });
        }
        setPendingAuditDates({});
        setIsEditingAuditDates(false);
        setEditingProposeDateStandardId(null);
        setAlert({
            type: 'warning',
            message: responseData?.error || `${fallbackMessage}: Audit Date is managed in Audit Scheduling.`,
        });
        return true;
    };

    const handleStartAuditDateEdit = () => {
        if (isAuditDateLocked) {
            setAlert({
                type: 'warning',
                message: 'Audit Date is locked because Audit Process has started. Update it in Audit Scheduling.',
            });
            return;
        }
        setIsEditingAuditDates(true);
    };

    const handleProposeDateSave = async (standardId) => {
        if (isAuditDateLocked) {
            handleStartAuditDateEdit();
            return;
        }
        setSavingProposeDate(true);
        try {
            const response = await api.patch(`/crm/projects/standards/${standardId}/propose-date`, {
                proposeDate: editingProposeDateValue,
            });
            const awareness = getAuditAwarenessFromResponse(response?.data);
            if (awareness) {
                setAuditDateAwareness(awareness);
            }
            setProjectData(prev => {
                if (!prev) return prev;
                const updateStdInList = (list) => list?.map(svc => ({
                    ...svc,
                    proposeDate: svc.id === standardId ? editingProposeDateValue : svc.proposeDate,
                    sub_services: svc.sub_services?.map(sub => ({
                        ...sub,
                        proposeDate: sub.id === standardId ? editingProposeDateValue : sub.proposeDate,
                    })),
                }));
                return {
                    ...prev,
                    project_sections: prev.project_sections?.map(sec => ({
                        ...sec,
                        services: updateStdInList(sec.services),
                    })),
                };
            });
            setEditingProposeDateStandardId(null);
            setAlert({ type: 'success', message: `Audit Date updated. ${summarizeAuditHandlers(awareness)}` });
        } catch (err) {
            if (handleAuditDateLockError(err)) return;
            const msg = err?.response?.data?.error || err.message;
            setAlert({ type: 'danger', message: `Failed to update Audit Date: ${msg}` });
        } finally {
            setSavingProposeDate(false);
        }
    };

    const handleAuditDateChange = (standardId, value) => {
        setPendingAuditDates(prev => ({ ...prev, [standardId]: value }));
    };

    const createIsccAuditProcessWhenReady = async (persistedProject) => {
        const projectIdToUse = realProjectId || persistedProject?.id || projectData?.id;
        if (!projectIdToUse || !isSustainabilityAuditProject(persistedProject)) return null;

        const readiness = buildProjectAuditReadiness(persistedProject);
        if (!readiness.ready) return null;

        try {
            const response = await api.post(`/crm/projects/${projectIdToUse}/audit-process/create`);
            const awareness = getAuditAwarenessFromResponse(response?.data);
            if (awareness) return awareness;
        } catch (error) {
            // Creation is idempotent from the user's perspective. If another save
            // already created this project's process, reload its tasks below.
            if (error?.response?.data?.code !== 'auditProcessAlreadyExists') {
                throw error;
            }
        }

        const tasksResponse = await api.get(`/audit/tasks?projectId=${projectIdToUse}`);
        return getAuditAwarenessFromTasks(tasksResponse?.data, projectIdToUse);
    };

    const handleSaveAllAuditDates = async () => {
        if (isAuditDateLocked) {
            handleStartAuditDateEdit();
            return;
        }
        const stateEntries = Object.entries(pendingAuditDates);
        // Native <input type="date"> controls can receive a value without
        // dispatching the React change event in browser automation/native-picker
        // integrations. Read the rendered controls as a fallback so Save always
        // uses the value the user can see.
        const domEntries = Object.entries(auditDateInputRefs.current)
            .map(([standardId, input]) => [standardId, input?.value || ''])
            .filter(([, proposeDate]) => proposeDate);
        const entryMap = new Map(domEntries);
        stateEntries.forEach(([standardId, proposeDate]) => entryMap.set(standardId, proposeDate));
        const entries = Array.from(entryMap.entries());
        if (entries.length === 0) {
            setIsEditingAuditDates(false);
            return;
        }
        setSavingProposeDate(true);
        try {
            // These dates describe one certification lifecycle. Saving each row in
            // parallel let whichever request committed first create the Audit Process,
            // which could incorrectly start Surveillance 1 before Initial Certification.
            // Submit them as one transaction so the backend can persist every cycle,
            // select the active scope once, then create exactly one initial task set.
            // A single-row edit must use the same endpoint as the inline editor.
            // The bulk endpoint is intended for saving a complete lifecycle and, in
            // older backend deployments, could return 200 without materializing the
            // active cycle. Using the canonical single-row flow here keeps the first
            // audit-date handoff atomic and ensures task orchestration runs.
            const response = entries.length === 1
                ? await api.patch(`/crm/projects/standards/${entries[0][0]}/propose-date`, {
                    proposeDate: entries[0][1],
                    projectId: realProjectId || projectData?.id,
                })
                : await api.patch('/crm/projects/audit-dates', {
                    updates: entries.map(([standardId, proposeDate]) => ({ standardId, proposeDate })),
                });

            if (entries.length === 1) {
                const savedDate = response?.data?.proposeDate || '';
                if (savedDate !== entries[0][1]) {
                    throw new Error('The server did not confirm the selected Audit Date.');
                }

                // Confirm the durable project payload before showing success. The
                // save response is not enough because the backend may acknowledge
                // the PATCH before the project-detail read model is updated.
                const persistedResponse = await api.get(`/crm/projects/${realProjectId || projectData?.id}`);
                const persistedProject = mapProjectDtoToViewModel(
                    persistedResponse.data,
                    projectData?.client?.contact_person,
                    projectData?.proposal_number,
                );
                const persistedRows = (persistedProject?.project_sections || [])
                    .flatMap((section) => section.services || [])
                    .flatMap((service) => [service, ...(service.sub_services || [])]);
                const persistedRow = persistedRows.find((row) => String(row.id) === String(entries[0][0]));
                if (persistedRow?.proposeDate !== entries[0][1]) {
                    throw new Error('Audit Date was accepted but did not persist after reload.');
                }
                setProjectData(persistedProject);
            }
            const projectIdToUse = realProjectId || projectData?.id;
            // Re-read the canonical model after either save path. In particular,
            // ISCC uses a bulk lifecycle-date save and the response may not include
            // enough context to decide whether all cycle rows are ready.
            const persistedResponse = await api.get(`/crm/projects/${projectIdToUse}`);
            const persistedProject = mapProjectDtoToViewModel(
                persistedResponse.data,
                projectData?.client?.contact_person,
                projectData?.proposal_number,
            );
            setProjectData(persistedProject);

            let awareness = getAuditAwarenessFromResponse(response?.data);
            const isIscc = isSustainabilityAuditProject(persistedProject);
            if (isIscc) {
                const isccAwareness = await createIsccAuditProcessWhenReady(persistedProject);
                awareness = isccAwareness || awareness;
            }
            if (awareness) {
                setAuditDateAwareness(awareness);
            }
            setPendingAuditDates({});
            setIsEditingAuditDates(false);
            setAlert({ type: 'success', message: awareness ? `Audit Dates updated. ${summarizeAuditHandlers(awareness)}` : 'Audit Dates updated successfully.' });
        } catch (err) {
            if (handleAuditDateLockError(err, 'Failed to update Audit Dates')) return;
            const msg = err?.response?.data?.error || err.message;
            setAlert({ type: 'danger', message: `Failed to update Audit Dates: ${msg}` });
        } finally {
            setSavingProposeDate(false);
        }
    };

    const handleCancelAuditDateEdit = () => {
        setPendingAuditDates({});
        setIsEditingAuditDates(false);
    };

    // Document preview/upload handlers
    const handleDocumentPreview = () => {
        if (projectData?.projectPdfFillPdf) {
            setDocumentPreview(projectData.projectPdfFillPdf);
        } else if (documentFile) {
            setDocumentPreview(URL.createObjectURL(documentFile));
        }
        setIsDocumentModalOpen(true);
    };

    const handleDocumentUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.type === 'application/pdf') {
            setDocumentFile(file);
            setDocumentPreview(URL.createObjectURL(file));
        } else {
            setAlert({ type: 'danger', message: 'Please select a valid PDF file.' });
        }
    };

    const openDocumentUploadModal = () => {
        setDocumentFile(null);
        setDocumentPreview(projectData?.projectPdfFillPdf || null);
        setIsDocumentModalOpen(true);
    };

    const closeDocumentModal = () => {
        setIsDocumentModalOpen(false);
        if (documentPreview && documentPreview.startsWith('blob:')) {
            URL.revokeObjectURL(documentPreview);
        }
        setDocumentPreview(null);
        setDocumentFile(null);
    };

    const saveDocument = async () => {
        if (!documentFile || !realProjectId) return;
        setUploadingDocument(true);
        try {
            const formData = new FormData();
            formData.append('file', documentFile);
            await api.post(`/crm/projects/${realProjectId}/pdf-fill`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setAlert({ type: 'success', message: 'Document uploaded successfully.' });
            closeDocumentModal();
            // Refresh project data
            const response = await api.get(`/crm/projects/${realProjectId}`);
            setProjectData(response.data);
        } catch (err) {
            setAlert({ type: 'danger', message: 'Failed to upload document: ' + (err.response?.data?.error || err.message) });
        } finally {
            setUploadingDocument(false);
        }
    };

    const updateProjectDates = async (startDate, endDate) => {
        if (!realProjectId) return;
        try {
            await api.put(`/crm/projects/${realProjectId}`, {
                projectDate: startDate,
                validUntil: endDate,
            });
        } catch (error) {
            setAlert({
                type: 'danger',
                message: 'Failed to update project dates: ' + (error.response?.data?.message || error.message)
            });
        }
    };

    const handleProjectStartDateChange = (e) => {
        const newDate = e.target.value;
        setProjectStartDate(newDate);
        updateProjectDates(newDate, projectEndDate);
    };

    const handleProjectEndDateChange = (e) => {
        const newDate = e.target.value;
        setProjectEndDate(newDate);
        updateProjectDates(projectStartDate, newDate);
    };

    // Initialize project dates when projectData is loaded
    useEffect(() => {
        if (projectData) {
            // Initialize dates from project data if available
            if (projectData.dates?.project_date) {
                setProjectStartDate(projectData.dates.project_date);
            }
            
            if (projectData.dates?.valid_until) {
                setProjectEndDate(projectData.dates.valid_until);
            }
        }
    }, [projectData]);

    useEffect(() => {
        const awareness = getAuditAwarenessFromResponse(projectData);
        if (!awareness || !awareness.handlers?.some((handler) => handler.taskId)) return;
        setAuditDateAwareness(awareness);
        setIsEditingAuditDates(false);
        setPendingAuditDates({});
    }, [projectData]);

    useEffect(() => {
        const projectIdToUse = realProjectId || projectData?.id;
        if (!projectIdToUse) return;

        let cancelled = false;
        const loadAuditDateAwareness = async () => {
            try {
                const response = await api.get(`/audit/tasks?projectId=${projectIdToUse}`);
                if (cancelled) return;
                const awareness = getAuditAwarenessFromTasks(response?.data, projectIdToUse);
                if (awareness) {
                    setAuditDateAwareness(awareness);
                    setIsEditingAuditDates(false);
                    setPendingAuditDates({});
                }
            } catch (error) {
                console.debug('Audit Date awareness unavailable:', error?.response?.status || error.message);
            }
        };

        loadAuditDateAwareness();
        return () => {
            cancelled = true;
        };
    }, [realProjectId, projectData?.id]);

    // Add missing state variables for send to client functionality - commented out
    // const [sendToClientDisabled, setSendToClientDisabled] = useState(false);
    // const [sendToClientLabel, setSendToClientLabel] = useState('Send to Client');

    // Add state untuk modal workflow (sama seperti di EditProposal) - commented out
    // const [showWorkflowModal, setShowWorkflowModal] = useState(false);
    // const [workflowItems, setWorkflowItems] = useState([]);
    // const [loadingWorkflow, setLoadingWorkflow] = useState(false);
    // const [alert, setAlert] = useState(null); // Already declared above with file management states



    // Add state for email functionality - commented out
    // const [emailSettings, setEmailSettings] = useState(null);
    // const [emailTemplates, setEmailTemplates] = useState([]);
    // const [isSendingEmail, setIsSendingEmail] = useState(false);
    // const [emailStatus, setEmailStatus] = useState(null); // Add this for email status
    // const [clientEmail, setClientEmail] = useState('');
    // const [emailDataLoaded, setEmailDataLoaded] = useState(false); // Add this flag
    // const [isLoadingEmailData, setIsLoadingEmailData] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                    // ✅ PERBAIKAN: Ambil proposalId dari proposal endpoint terlebih dahulu
                    const proposalRes = await api.get(`/crm/proposals/detail/lead/${id}`);

                    if (proposalRes.data) {
                        const proposalId = proposalRes.data.id;
                        const proposalNumber = proposalRes.data.proposal_number || proposalRes.data.proposalCode;
                        if (proposalRes.data.currencyCode || proposalRes.data.currency_code) {
                            setProposalCurrencyCode(proposalRes.data.currencyCode || proposalRes.data.currency_code);
                        }

                        // ✅ FIX: Fetch lead data to get companyId and contact person
                        let leadCompanyId = null;
                        let leadContactPerson = null;
                        try {
                            const leadRes = await api.get(`/crm/leads/${id}`);
                            setLeadData(leadRes.data); // Store lead data for project name display
                            if (leadRes.data?.companyId) {
                                leadCompanyId = leadRes.data.companyId;
                            } else if (leadRes.data?.company?.id) {
                                leadCompanyId = leadRes.data.company.id;
                            }
                            // ✅ FIX: Get contact person from lead
                            if (leadRes.data?.contact?.firstName && leadRes.data?.contact?.lastName) {
                                leadContactPerson = `${leadRes.data.contact.firstName} ${leadRes.data.contact.lastName}`.trim();
                            } else if (leadRes.data?.contactPerson) {
                                leadContactPerson = leadRes.data.contactPerson;
                            } else if (leadRes.data?.contact?.name) {
                                leadContactPerson = leadRes.data.contact.name;
                            }
                        } catch (leadError) {
                            console.error('Error fetching lead data:', leadError);
                            // Silently handle error - will try other sources
                        }
                    
                    // ✅ PERBAIKAN: Coba endpoint project berdasarkan proposalId
                    try {
                        const projectRes = await api.get(`/crm/projects/by-proposal/${proposalId}`);
                        
                        if (projectRes.data) {
                            // ✅ FIX: Use proposal response's contact person first (like DetailProposal does)
                            const contactPerson = proposalRes.data?.client?.contact_person ||
                                                   proposalRes.data?.contactPerson ||
                                                   leadContactPerson;
                            const vm = mapProjectDtoToViewModel(projectRes.data, contactPerson, proposalNumber);
                            setProjectData(vm);
                            setRealProjectId(projectRes.data.id); // ✅ Store real project ID
                            
                            // Store company ID - try project response first, then lead, then proposal response
                            if (projectRes.data.companyId) {
                                setCompanyId(projectRes.data.companyId);
                            } else if (projectRes.data.client?.id) {
                                setCompanyId(projectRes.data.client.id);
                            } else if (leadCompanyId) {
                                setCompanyId(leadCompanyId);
                            } else if (proposalRes.data.companyId) {
                                setCompanyId(proposalRes.data.companyId);
                            } else if (proposalRes.data.client?.id) {
                                setCompanyId(proposalRes.data.client.id);
                            }

                            if (vm && vm.project_sections) {
                                await fetchYearOptionsForProject(vm.project_sections);
                            }

                            // ✅ TAMBAHAN: Fetch project history
                            await fetchProjectHistory(id);
                        } else {
                            throw new Error('Project data not found');
                        }
                    } catch (projectError) {
                        // Fallback to proposal data if project endpoint fails
                        
                        // ✅ FALLBACK: Gunakan data proposal jika project belum ter-convert
                        // ✅ FIX: Use proposal response's contact person first (like DetailProposal does)
                        const contactPerson = proposalRes.data?.client?.contact_person ||
                                               proposalRes.data?.contactPerson ||
                                               leadContactPerson;
                        const vm = mapProposalDtoToProjectViewModel(proposalRes.data, contactPerson, proposalNumber);
                        setProjectData(vm);
                        setRealProjectId(proposalRes.data.id); // ✅ Use proposal ID as fallback
                        
                        // Store company ID - try lead first, then proposal response
                        if (leadCompanyId) {
                            setCompanyId(leadCompanyId);
                        } else if (proposalRes.data.companyId) {
                            setCompanyId(proposalRes.data.companyId);
                        } else if (proposalRes.data.client?.id) {
                            setCompanyId(proposalRes.data.client.id);
                        }

                        if (vm && vm.project_sections) {
                            await fetchYearOptionsForProject(vm.project_sections);
                        }

                        // ✅ TAMBAHAN: Fetch project history
                        await fetchProjectHistory(id);
                    }
                } else {
                    throw new Error('Proposal data not found');
                }
            } catch (error) {
                console.error('Error fetching project data:', error);
                setAlert({
                    type: 'danger',
                    message: 'Failed to load project data: ' + (error.response?.data?.message || error.message)
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    // ✅ TAMBAHAN: Map proposal DTO to project view model (untuk fallback)
    const mapProposalDtoToProjectViewModel = (dto, leadContactPerson = null, proposalNumber = null) => {
        if (!dto) return null;


        // Build project_sections from proposal_sections
        let project_sections = [];

        // ✅ TAMBAHAN: Coba berbagai kemungkinan struktur data
        if (Array.isArray(dto.proposal_sections) && dto.proposal_sections.length > 0) {
            dto.proposal_sections.forEach((section, sectionIndex) => {
                if (Array.isArray(section.services)) {
                    const mappedServices = [];
                    section.services.forEach((service, serviceIndex) => {
                        const mappedService = {
                            id: service.id ?? service.standardId,
                            standard: service.standard || service.standardName || service.standard_name || '',
                            product_type: service.product_type || service.productCategoryName || service.product_category_name || '',
                            service_item: service.service_item || service.serviceItemName || service.service_item_name || '',
                            serviceItemId: service.serviceItemId || service.service_item_id || undefined,
                            qty: service.qty || service.quantity || undefined,
                            unit: service.unit || undefined,
                            proposeDate: service.proposeDate || service.proposed_date || service.propose_date || undefined,
                            sub_services: Array.isArray(service.sub_services) 
                                ? service.sub_services.map((subService) => ({
                                    id: subService.id ?? subService.standardId,
                                    // ✅ FIX: Add multiple fallback field names to handle different backend response formats
                                    product_type: subService.productCategoryName || 
                                                 subService.productCategory?.name || 
                                                 subService.product_type || 
                                                 subService.productCategory || 
                                                 subService.product_category_name || 
                                                 '',
                                    service_item: subService.serviceItemName || 
                                                 subService.serviceItem?.name || 
                                                 subService.service_item || 
                                                 subService.serviceItem || 
                                                 subService.service_item_name || 
                                                 '',
                                    qty: subService.qty || subService.quantity || undefined,
                                    unit: subService.unit || 
                                          subService.unitType?.name || 
                                          subService.unit_name || 
                                          undefined,
                                    proposeDate: subService.proposeDate || 
                                                subService.proposed_date || 
                                                subService.propose_date || 
                                                undefined,
                                }))
                                : undefined,
                        };
                        mappedServices.push(mappedService);
                    });
                    
                    // Create project section with mapped services
                    const projectSection = {
                        id: section.id || sectionIndex + 1,
                        type: section.type || 'Services',
                        services: mappedServices,
                    };
                    project_sections.push(projectSection);
                } else {
                }
            });
        } else {
            // ✅ TAMBAHAN: Coba struktur alternatif
            if (Array.isArray(dto.services)) {
                const mappedServices = [];
                dto.services.forEach((service, index) => {
                    const mappedService = {
                            id: service.id ?? service.standardId,
                        standard: service.standard || service.standardName || service.standard_name || '',
                        product_type: service.product_type || service.productCategoryName || service.product_category_name || '',
                        service_item: service.service_item || service.serviceItemName || service.service_item_name || '',
                        serviceItemId: service.serviceItemId || service.service_item_id || undefined,
                        qty: service.qty || service.quantity || undefined,
                        unit: service.unit || undefined,
                        proposeDate: service.proposeDate || service.proposed_date || service.propose_date || undefined,
                    };
                    mappedServices.push(mappedService);
                });
                
                // Create single project section
                project_sections.push({
                    id: 1,
                    type: 'Services',
                    services: mappedServices,
                });
            }
        }


        const vm = {
            id: dto.id,
            project_number: dto.project_number || dto.projectCode || dto.proposal_number || `PROJ-${dto.id}`,
            proposal_number: proposalNumber || dto.proposal_number || dto.project_number, // Store actual proposal number
            // Map status fields - use correct field names from proposal endpoint
            status: dto.statusDesc || dto.statusApproval || dto.status || 'Draft',
            statusProposal: dto.statusProposalDesc || dto.statusProposal || 'Draft',
            statusProject: dto.statusProjectDesc || dto.statusProject || 'Draft',
            client: {
                name: dto.client?.name || dto.companyName || dto.company_name || '',
                address: dto.client?.address || dto.companyAddress || dto.company_address || '',
                // ✅ FIX: Prioritize passed contactPerson (from proposal response) over DTO fields
                contact_person: leadContactPerson || dto.client?.contact_person || dto.contactPerson || dto.contact_person || '',
                company_name: dto.client?.name || dto.companyName || dto.company_name || '',
                company_email: dto.client?.companyEmail || dto.client?.contactEmail || dto.companyEmail || dto.company_email || '',
            },
            dates: {
                project_date: dto.dates?.proposal_date || dto.dates?.project_date || dto.projectDate || dto.project_date || '',
                valid_until: dto.dates?.valid_until || dto.validUntil || dto.valid_until || '',
                created_at: dto.dates?.created_at || dto.createdAt || dto.created_at || '',
            },
            assigned_to: {
                id: dto.assignedToUser?.id || dto.assigned_to?.id || null,
                name: dto.assignedToUser?.fullName || dto.assigned_to?.name || dto.assignedTo || dto.assigned_to || '',
                team: dto.assignedToUser?.departmentName || dto.assigned_to?.team || dto.assigned_to?.departmentName || dto.departmentName || dto.team || '',
            },
            project_sections: project_sections,
            template: {
                name: dto.template?.name || dto.projectTemplateName || dto.project_template_name || '',
                type: dto.template?.type || dto.projectTemplateType || dto.project_template_type || '',
            },
            grandTotal: dto.grandTotal || dto.grand_total || 0,
            multiSite: dto.multiSite || dto.multi_site || false,
            auditFlaggingType: dto.auditFlaggingType || dto.audit_flagging_type || 'none',
            auditMethodology: dto.auditMethodology || dto.audit_methodology || '',
            auditConfigurationCompleted: dto.auditConfigurationCompleted || dto.audit_configuration_completed || false,
            projectPdfFill: dto.proposalPdfFill || dto.projectPdfFill || dto.proposal_pdf_fill || null,
            projectPdfFillPdf: dto.proposalPdfFillPdf || dto.projectPdfFillPdf || dto.proposal_pdf_fill_pdf || null,
            history: Array.isArray(dto.history) ? dto.history : [],
            auditDateAwareness: dto.auditDateAwareness || dto.audit_date_awareness || null,
        };

        return vm;
    };

    // Map backend DTO to the view model structure used by this component
    const mapProjectDtoToViewModel = (dto, leadContactPerson = null, proposalNumber = null) => {
        if (!dto) return null;


        // Build project_sections with multiple fallbacks
        let project_sections = [];

        // 1) If backend already sends project_sections in expected format, reuse it
        if (Array.isArray(dto.project_sections) && dto.project_sections.length > 0) {
            dto.project_sections.forEach((section, sectionIndex) => {
                if (Array.isArray(section.services)) {
                    const mappedServices = [];
                    section.services.forEach((service) => {
                        const mappedService = {
                            id: service.id ?? service.standardId,
                            standard: service.standard || service.standardName || '',
                            product_type: service.product_type || service.productCategoryName || '',
                            service_item: service.service_item || service.serviceItemName || '',
                            serviceItemId: service.serviceItemId || undefined,
                            qty: service.qty || undefined,
                            unit: service.unit || undefined,
                            proposeDate: service.proposeDate || service.proposed_date || undefined,
                            sub_services: Array.isArray(service.sub_services) 
                                ? service.sub_services.map((subService) => ({
                                    id: subService.id ?? subService.standardId,
                                    // ✅ FIX: Add multiple fallback field names to handle different backend response formats
                                    product_type: subService.productCategoryName || 
                                                 subService.productCategory?.name || 
                                                 subService.product_type || 
                                                 subService.productCategory || 
                                                 '',
                                    service_item: subService.serviceItemName || 
                                                 subService.serviceItem?.name || 
                                                 subService.service_item || 
                                                 subService.serviceItem || 
                                                 '',
                                    qty: subService.qty || undefined,
                                    unit: subService.unit || 
                                          subService.unitType?.name || 
                                          subService.unit_name || 
                                          undefined,
                                    proposeDate: subService.proposeDate || 
                                                subService.proposed_date || 
                                                subService.propose_date || 
                                                undefined,
                                }))
                                : undefined,
                        };
                        mappedServices.push(mappedService);
                    });
                    
                    project_sections.push({
                        id: section.id || sectionIndex + 1,
                        type: section.type || 'Services',
                        services: mappedServices,
                    });
                }
            });
        }

        // 2) If backend sends proposal_sections (fallback), use it
        if (project_sections.length === 0 && Array.isArray(dto.proposal_sections) && dto.proposal_sections.length > 0) {
            dto.proposal_sections.forEach((section, sectionIndex) => {
                if (Array.isArray(section.services)) {
                    const mappedServices = [];
                    section.services.forEach((service) => {
                        const mappedService = {
                            id: service.id ?? service.standardId,
                            standard: service.standard || service.standardName || '',
                            product_type: service.product_type || service.productCategoryName || '',
                            service_item: service.service_item || service.serviceItemName || '',
                            serviceItemId: service.serviceItemId || undefined,
                            qty: service.qty || undefined,
                            unit: service.unit || undefined,
                            proposeDate: service.proposeDate || service.proposed_date || undefined,
                            sub_services: Array.isArray(service.sub_services) 
                                ? service.sub_services.map((subService) => ({
                                    id: subService.id ?? subService.standardId,
                                    // ✅ FIX: Add multiple fallback field names to handle different backend response formats
                                    product_type: subService.productCategoryName || 
                                                 subService.productCategory?.name || 
                                                 subService.product_type || 
                                                 subService.productCategory || 
                                                 '',
                                    service_item: subService.serviceItemName || 
                                                 subService.serviceItem?.name || 
                                                 subService.service_item || 
                                                 subService.serviceItem || 
                                                 '',
                                    qty: subService.qty || undefined,
                                    unit: subService.unit || 
                                          subService.unitType?.name || 
                                          subService.unit_name || 
                                          undefined,
                                    proposeDate: subService.proposeDate || 
                                                subService.proposed_date || 
                                                subService.propose_date || 
                                                undefined,
                                }))
                                : undefined,
                        };
                        mappedServices.push(mappedService);
                    });
                    
                    project_sections.push({
                        id: section.id || sectionIndex + 1,
                        type: section.type || 'Services',
                        services: mappedServices,
                    });
                }
            });
        }

        // 3) Else, build from projectServices/standards (DTO structure) - fallback
        if (project_sections.length === 0 && Array.isArray(dto.projectServices)) {
            dto.projectServices.forEach((svc, svcIndex) => {
                const standards = Array.isArray(svc.standards) ? svc.standards : [];
                const mappedServices = [];

                if (standards.length > 0) {
                    standards.forEach((std, stdIndex) => {
                        const mapChild = (child) => {
                            const mapped = {
                                id: child?.id ?? child?.standardId,
                                // ✅ FIX: Add multiple fallback field names to handle different backend response formats
                                product_type: child?.productCategoryName ||
                                             child?.productCategory?.name ||
                                             child?.product_type ||
                                             child?.productCategory ||
                                             '',
                                service_item: child?.serviceItemName ||
                                             child?.serviceItem?.name ||
                                             child?.service_item ||
                                             child?.serviceItem ||
                                             '',
                                qty: child?.qty || undefined,
                                unit: child?.unit ||
                                      child?.unitType?.name ||
                                      child?.unit_name ||
                                      undefined,
                                proposeDate: child?.proposeDate ||
                                            child?.proposed_date ||
                                            child?.propose_date ||
                                            undefined,
                                selectedYear: child?.selectedYear || undefined,
                            };
                            return mapped;
                        };

                        mappedServices.push({
                            id: std?.id ?? std?.standardId,
                            standard: std?.standardName || '',
                            product_type: std?.productCategoryName || '',
                            service_item: std?.serviceItemName || '',
                            serviceItemId: std?.serviceItemId || undefined,
                            qty: std?.qty || undefined,
                            unit: std?.unit || undefined,
                            proposeDate: std?.proposeDate || std?.proposed_date || undefined,
                            selectedYear: std?.selectedYear || undefined,
                            sub_services: Array.isArray(std?.children) ? std.children.map(mapChild) : undefined,
                        });
                    });
                } else {
                    // Fallback: no standards provided, still show the service line
                    mappedServices.push({
                        standard: '',
                        product_type: '',
                        service_item: svc?.serviceName || `Service ${svc?.serviceId || ''}`,
                        serviceItemId: undefined,
                        qty: undefined,
                        unit: undefined,
                        proposeDate: undefined,
                        sub_services: undefined,
                    });
                }
                
                // ✅ FIX: Create a section for each service with its serviceName as the type
                const section = {
                    id: svc?.id || svcIndex + 1,
                    type: svc?.serviceName || (svcIndex === 0 ? 'Services' : 'Additional Services'),
                    services: mappedServices,
                };
                project_sections.push(section);
            });
        }


        const vm = {
            id: dto.id,
            project_number: dto.project_number || dto.projectCode || dto.proposal_number || `PROJ-${dto.id}`,
            proposal_number: proposalNumber || dto.proposal_number || dto.project_number, // Store actual proposal number
            // Map status fields - use correct field names from project endpoint
            status: dto.statusDesc || dto.statusApproval || dto.status || 'Draft',
            statusProposal: dto.statusProposalDesc || dto.statusProposal || dto.statusDesc || 'Draft',
            statusProject: dto.statusProjectDesc || dto.statusProject || dto.statusDesc || 'Draft',
            client: {
                name: dto.client?.name || dto.companyName || '',
                address: dto.client?.address || dto.companyAddress || '',
                // ✅ FIX: Prioritize passed contactPerson (from proposal response) over DTO fields
                contact_person: leadContactPerson || dto.client?.contact_person || dto.contactPerson || '',
                company_name: dto.client?.name || dto.companyName || '',
                company_email: dto.client?.companyEmail || dto.client?.contactEmail || dto.companyEmail || '',
            },
            dates: {
                project_date: dto.dates?.project_date || dto.dates?.proposal_date || dto.projectDate || dto.project_date || '',
                valid_until: dto.dates?.valid_until || dto.validUntil || dto.valid_until || '',
                created_at: dto.dates?.created_at || dto.createdAt || '',
            },
            assigned_to: {
                id: dto.assignedToUser?.id || dto.assigned_to?.id || null,
                name: dto.assignedToUser?.fullName || dto.assigned_to?.name || dto.assignedTo || '',
                team: dto.assignedToUser?.departmentName || dto.assigned_to?.team || dto.assigned_to?.departmentName || dto.departmentName || dto.team || '',
            },
            project_sections: project_sections,
            template: {
                name: dto.template?.name || dto.projectTemplateName || '',
                type: dto.template?.type || dto.projectTemplateType || '',
            },
            grandTotal: dto.grandTotal || 0,
            multiSite: dto.multiSite || dto.multi_site || false,
            auditFlaggingType: dto.auditFlaggingType || dto.audit_flagging_type || 'none',
            auditMethodology: dto.auditMethodology || dto.audit_methodology || '',
            auditConfigurationCompleted: dto.auditConfigurationCompleted || dto.audit_configuration_completed || false,
            projectPdfFill: dto.projectPdfFill || dto.proposalPdfFill || null,
            projectPdfFillPdf: dto.projectPdfFillPdf || dto.proposalPdfFillPdf || null,
            history: Array.isArray(dto.history) ? dto.history : [],
            auditDateAwareness: dto.auditDateAwareness || dto.audit_date_awareness || null,
        };

        return vm;
    };

    // Function to fetch year options for all service items in the project
    const fetchYearOptionsForProject = async (projectSections) => {
        try {
            // Fetch investment breakdown from API
            await fetchInvestmentBreakdown();
        } catch (err) {
            // Silently handle error
        }
    };

    // Function to fetch year option for a specific service item
    const fetchYearOption = async (serviceItemId) => {
        if (!serviceItemId) return;

        try {
            const res = await api.get(`/md/audit-type/year-option/${serviceItemId}`);
            const yearOption = res.data;

            // Store year option data in state
            setYearOptionsByServiceItem(prev => ({
                ...prev,
                [serviceItemId]: yearOption
            }));

            return yearOption;
        } catch (err) {
            console.error('Error fetching year option:', err);
        }
    };

    // ✅ TAMBAHAN: Function to fetch project history from API
    const fetchProjectHistory = async (projectId) => {
        try {
            setLoadingHistory(true);

            // Try project endpoint first
            let response;
            try {
                response = await api.get(`/crm/projects/${projectId}/history`);
            } catch (projectError) {
                throw projectError;
            }

            if (response.data && Array.isArray(response.data)) {
                // Map API response to frontend format
                const mappedHistory = response.data.map((history) => ({
                    id: history.id,
                    type: history.type,
                    date: history.date,
                    user: history.user,
                    notes: history.notes,
                    createdAt: history.createdAt
                }));

                setProjectHistory(mappedHistory);
            } else {
                setProjectHistory([]);
            }
        } catch (error) {
            console.error('Error fetching project history:', error);
            setProjectHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Calculate investment breakdown dynamically based on service item year options
    // Fetch investment breakdown from API
    const fetchInvestmentBreakdown = async () => {
        setLoadingBreakdown(true);
        try {

            // The project breakdown endpoint (/crm/projects/{id}/breakdown) returns a different,
            // incompatible shape ({projectId, services: [...]}) — it has no years/breakdown array,
            // so it can never populate this table. The proposal breakdown endpoint
            // (/crm/proposals/{leadId}/breakdown) is the one that returns {years, breakdown, totals}.
            // Validate the shape rather than trusting whichever call merely didn't throw.
            let response;
            try {
                response = await api.get(`/crm/projects/${id}/breakdown`);
                if (!Array.isArray(response.data?.years) || !Array.isArray(response.data?.breakdown)) {
                    throw new Error('project breakdown endpoint returned an incompatible shape');
                }
            } catch (projectError) {
                response = await api.get(`/crm/proposals/${id}/breakdown`);
            }

            if (response.data && Array.isArray(response.data.years) && Array.isArray(response.data.breakdown)) {
                // Use the exchange-rate snapshot saved on the linked proposal
                // rather than the mutable currency-rate table.
                const proposalResponse = await api.get(`/crm/proposals/detail/lead/${id}`).catch(() => ({ data: null }));
                const proposalRate = Number(proposalResponse.data?.exchangeRateAtCreation);
                const savedExchangeRate = Number.isFinite(proposalRate) && proposalRate > 0 ? proposalRate : 1;

                const breakdown = response.data.breakdown;
                breakdown.forEach(serviceRow => serviceRow.standards?.forEach(stdRow =>
                    stdRow.serviceItems?.forEach(item => {
                        const currencyCode = item.currencyCode || 'IDR';
                        const rawAmount = Number(item.unitPrice || 0) * Number(item.qty ?? 1);
                        const exchangeRate = currencyCode.toUpperCase() === 'IDR' ? 1 : savedExchangeRate;
                        const convertedAmount = currencyCode.toUpperCase() === 'IDR'
                            ? rawAmount : rawAmount * exchangeRate;
                        item.exchangeRate = exchangeRate;
                        item.prices = (item.years || []).map(() => convertedAmount);
                    })
                ));
                response.data.totals = (response.data.years || []).map(year =>
                    breakdown.reduce((sum, serviceRow) => serviceRow.standards?.reduce((standardSum, stdRow) =>
                        standardSum + (stdRow.serviceItems || []).reduce((itemSum, item) => {
                            const yearIndex = (item.years || []).indexOf(year);
                            return itemSum + (yearIndex >= 0 ? Number(item.prices[yearIndex] || 0) : 0);
                        }, 0), 0) || 0, 0)
                );
                setInvestmentBreakdown(response.data);
                setShowBreakdown(true);
            } else {
                setShowBreakdown(false);
                setInvestmentBreakdown(null);
            }
        } catch (error) {
            console.error('Error fetching investment breakdown:', error);
            setShowBreakdown(false);
            setInvestmentBreakdown(null);
        } finally {
            setLoadingBreakdown(false);
        }
    };

    // Fetch investment breakdown when year options change
    useEffect(() => {
        if (projectData && projectData.project_sections && Object.keys(yearOptionsByServiceItem).length > 0) {
            // Add a small delay to ensure all year options are loaded
            const timer = setTimeout(() => {
                fetchInvestmentBreakdown();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [yearOptionsByServiceItem, projectData]);

    // Load email settings and templates with better error handling - commented out
    // useEffect(() => {
    //     loadEmailData();
    // }, []);



    // Update send to client function to include email sending - commented out
    // const onSendToClient = async () => {
    //     if (window.confirm('Are you sure you want to send this project to the client?')) {
    //         setLoadingWorkflow(true);
    //         try {
    //             // Fetch workflow items yang perlu review
    //             const response = await api.get(`/crm/projects/${id}/workflow-review`);
    //             setWorkflowItems(response.data);
    //             setShowWorkflowModal(true);
    //         } catch (error) {
    //             console.error('Error fetching workflow items:', error);
    //             setAlert({
    //                 type: 'danger',
    //                 message: 'Failed to fetch workflow items for review.'
    //             });
    //         } finally {
    //             setLoadingWorkflow(false);
    //         }
    //     }
    // };

    // const handleWorkflowModalClose = () => {
    //     setShowWorkflowModal(false);
    //     setWorkflowItems([]);
    // };

    // Update handleConfirmSendToClient dengan auto-reload - commented out
    // const handleConfirmSendToClient = async () => {
    //     try {
    //         setIsSendingEmail(true);
    //         setEmailStatus(null);
    //         

    // const handleSetNeedApproval = async () => {
    //     try {
    //         await api.post(`/crm/projects/${id}/set-need-approval`);
    //         setAlert({ type: 'success', message: 'Project status updated to Need Approval!' });
    //         
    //         // Update state tombol langsung
    //         setSendToClientDisabled(true);
    //         setSendToClientLabel('Need Approval');
    //         
    //         // Update project data status juga
    //         setProjectData(prev => {
    //             const updated = {
    //                 ...prev,
    //                 status: 3 // Set status ke 3
    //             };
    //             return updated;
    //         });
    //         
    //         setShowWorkflowModal(false);
    //         setWorkflowItems([]);
    //     } catch (error) {
    //         setAlert({ type: 'danger', message: 'Failed to update project status.' });
    //     }
    // };


    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/projects');
        }
    };

    const handleNavigateToLeadDetail = (leadId) => {
        navigate(`/leads/detail/${leadId}`);
    };

    const handleNavigateToProposal = (leadId) => {
        navigate(`/proposals/detail/${leadId}`);
    };



    const handleEdit = () => {
        navigate(`/projects/edit/${id}`);
    };




    if (loading) {
        return (
            <div className="container-xl">
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!projectData) {
        return (
            <div className="container-xl">
                <div className="text-center p-5">
                    <h3>Project not found</h3>
                </div>
            </div>
        );
    }

    const auditReadiness = buildProjectAuditReadiness(projectData);
    // "Audit Type" reflects the audit methodology. Integrated/Combined are derived
    // (backend) from auditMethodology and only apply when a project has more than
    // one standard. For a single-standard project the flagging type is 'none', which
    // is NOT "missing data" — it's a Single Audit. Fall back to the actual
    // methodology (default 'Single Audit') instead of the misleading "Not selected".
    const auditFlagLabel = projectData.auditFlaggingType === 'integrated'
        ? 'Integrated Audit'
        : projectData.auditFlaggingType === 'combined'
            ? 'Combined Audit'
            : (projectData.auditMethodology && projectData.auditMethodology.trim()
                ? projectData.auditMethodology
                : 'Single Audit');

    const renderServiceTable = (services, title, type = null) => {
        return (
            <div className="card mt-3">
                <div className="card-body">
                    <div className="col-12">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h4 className="mb-0">{title}</h4>
                            {isEditingAuditDates ? (
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-success"
                                        disabled={savingProposeDate}
                                        onClick={handleSaveAllAuditDates}
                                    >
                                        {savingProposeDate ? (
                                            <><span className="spinner-border spinner-border-sm me-1" role="status" />Saving...</>
                                        ) : 'Save'}
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary"
                                        disabled={savingProposeDate}
                                        onClick={handleCancelAuditDateEdit}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                canProject('edit') && (
                                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                                        {isAuditDateLocked && (
                                            <span className="badge bg-warning text-dark d-inline-flex align-items-center gap-1">
                                                <Lock size={12} aria-hidden="true" />
                                                Managed in Audit Scheduling
                                            </span>
                                        )}
                                        <button
                                            className="btn btn-outline-primary"
                                            disabled={isAuditDateLocked}
                                            onClick={handleStartAuditDateEdit}
                                            title={isAuditDateLocked ? 'Audit Date is locked after Audit Process starts' : 'Edit Audit Dates'}
                                        >
                                            {isAuditDateLocked ? (
                                                <Lock size={14} className="me-1" aria-hidden="true" />
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                    <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"/>
                                                    <path d="M13.5 6.5l4 4"/>
                                                </svg>
                                            )}
                                            Edit Audit Dates
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                        {type && (
                            <div className="col-md-4 mb-3">
                                <div className="form-control">{type}</div>
                            </div>
                        )}
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <colgroup>
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '15%' }} />
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '15%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: '200px' }}>Standard</th>
                                        <th style={{ minWidth: '200px' }}>Product Category</th>
                                        <th style={{ minWidth: '200px' }}>Service Item</th>
                                        <th style={{ minWidth: '100px' }}>Qty</th>
                                        <th style={{ minWidth: '100px' }}>Unit</th>
                                        <th style={{ minWidth: '180px' }}>Audit Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {services && services.length > 0 ? (
                                        services.map((service, index) => {
                                            return (
                                                <React.Fragment key={index}>
                                                    <tr>
                                                        <td>{service.standard}</td>
                                                        <td>{service.product_type}</td>
                                                        <td>{service.service_item}</td>
                                                        <td>{service.qty || '-'}</td>
                                                        <td>{service.unit || '-'}</td>
                                                        <td>
                                                            {isEditingAuditDates ? (
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={pendingAuditDates[String(service.id)] !== undefined ? pendingAuditDates[String(service.id)] : (service.proposeDate || '')}
                                                                    onChange={e => handleAuditDateChange(String(service.id), e.target.value)}
                                                                    ref={input => { if (input) auditDateInputRefs.current[String(service.id)] = input; }}
                                                                    style={{ minWidth: 140 }}
                                                                />
                                                            ) : (
                                                                <span>
                                                                    {(service.proposeDate && service.proposeDate.trim()) ? service.proposeDate : <span className="text-muted">-</span>}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {service.sub_services && sortCrmCycleRows(service.sub_services).map((subService, subIndex) => (
                                                        <tr key={`${index}-${subIndex}`} className="table-light">
                                                            <td className="text-end pe-2">
                                                                <small className="text-muted">↳</small>
                                                            </td>
                                                            <td>{subService.product_type}</td>
                                                            <td>{subService.service_item}</td>
                                                            <td>{subService.qty || '-'}</td>
                                                            <td>{subService.unit || '-'}</td>
                                                            <td>
                                                                {isEditingAuditDates ? (
                                                                    <input
                                                                        type="date"
                                                                        className="form-control form-control-sm"
                                                                        value={pendingAuditDates[String(subService.id)] !== undefined ? pendingAuditDates[String(subService.id)] : (subService.proposeDate || '')}
                                                                        onChange={e => handleAuditDateChange(String(subService.id), e.target.value)}
                                                                        ref={input => { if (input) auditDateInputRefs.current[String(subService.id)] = input; }}
                                                                        style={{ minWidth: 140 }}
                                                                    />
                                                                ) : (
                                                                    <span>
                                                                        {(subService.proposeDate && subService.proposeDate.trim()) ? subService.proposeDate : <span className="text-muted">-</span>}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted">
                                                No services found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    };


    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Load files when Files tab is activated
        if (tabId === 'tabs-files' && files.length === 0) {
            loadFiles();
        }
        // Load expenses when Expense tab is activated
        const projectIdToUse = realProjectId || id;
        if (tabId === 'tabs-expense' && expenses.length === 0 && projectIdToUse) {
            loadExpenses();
        }
        // Load invoices when Invoice tab is activated
        if (tabId === 'tabs-invoice' && invoices.length === 0 && projectIdToUse) {
            loadInvoices();
        }
        // Load investment breakdown when its tab is activated
        if (tabId === 'tabs-investment-breakdown' && !investmentBreakdown) {
            fetchInvestmentBreakdown();
        }
    };

    // Function to load expenses for this project
    const loadExpenses = async () => {
        // Use realProjectId if available, otherwise fallback to id (lead ID)
        const projectIdToUse = realProjectId || id;
        if (!projectIdToUse) return;
        setLoadingExpenses(true);
        try {
            const data = await getExpensesByProjectId(projectIdToUse);
            setExpenses(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading expenses:', error);
            setAlert({
                type: 'danger',
                message: 'Failed to load expenses: ' + (error.response?.data?.message || error.message)
            });
        } finally {
            setLoadingExpenses(false);
        }
    };

    // Function to handle expense form submit
    const handleExpenseSubmit = async (formData) => {
        try {
            if (selectedExpense) {
                await updateExpense(selectedExpense.id, formData);
                setAlert({
                    type: 'success',
                    message: 'Expense updated successfully'
                });
            } else {
                await createExpense(formData);
                setAlert({
                    type: 'success',
                    message: 'Expense created successfully'
                });
            }
            await loadExpenses();
            setIsExpenseModalOpen(false);
            setSelectedExpense(null);
        } catch (error) {
            console.error('Error saving expense:', error);
            setAlert({
                type: 'danger',
                message: 'Failed to save expense: ' + (error.response?.data?.message || error.message)
            });
        }
    };

    // Function to handle expense edit
    const handleExpenseEdit = (row) => {
        setSelectedExpense(row);
        setIsExpenseModalOpen(true);
    };

    // Function to handle expense delete
    const handleExpenseDelete = async (row) => {
        if (window.confirm(`Are you sure you want to delete this expense?`)) {
            try {
                await deleteExpense(row.id);
                setAlert({
                    type: 'success',
                    message: 'Expense deleted successfully'
                });
                await loadExpenses();
            } catch (error) {
                console.error('Error deleting expense:', error);
                setAlert({
                    type: 'danger',
                    message: 'Failed to delete expense: ' + (error.response?.data?.message || error.message)
                });
            }
        }
    };

    // Function to load invoices for this project
    const loadInvoices = async () => {
        // Use realProjectId if available, otherwise fallback to id (lead ID)
        const projectIdToUse = realProjectId || id;
        if (!projectIdToUse) return;
        setLoadingInvoices(true);
        try {
            const response = await api.get(`/crm/invoices/project/${projectIdToUse}`);
            setInvoices(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error loading invoices:', error);
            setAlert({
                type: 'danger',
                message: 'Failed to load invoices: ' + (error.response?.data?.message || error.message)
            });
        } finally {
            setLoadingInvoices(false);
        }
    };

    // Format date helper
    const formatDateToDDMMYYYY = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    // Format currency helper
    const formatCurrency = (amount, currencyCode = 'IDR') => {
        if (!amount) return '-';
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount)) return '-';
        
        const locale = currencyCode === 'USD' ? 'en-US' : 
                       currencyCode === 'EUR' ? 'de-DE' :
                       currencyCode === 'GBP' ? 'en-GB' :
                       currencyCode === 'JPY' ? 'ja-JP' :
                       currencyCode === 'SGD' ? 'en-SG' :
                       currencyCode === 'AUD' ? 'en-AU' :
                       'id-ID';
        
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 0
        }).format(numAmount);
    };

    // Expense table columns
    const expenseColumns = [
        { 
            key: 'expenseDate', 
            label: 'Date',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '120px', width: '120px' }}
                    title={formatDateToDDMMYYYY(value)}
                >
                    {formatDateToDDMMYYYY(value)}
                </span>
            )
        },
        { 
            key: 'categoryName', 
            label: 'Category',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '150px', width: '150px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        { 
            key: 'description', 
            label: 'Description',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '250px', width: '250px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        { 
            key: 'fileName', 
            label: 'File',
            className: 'text-truncate',
            render: (value, row) => {
                if (!value) return '-';
                return (
                    <button
                        type="button"
                        className="btn btn-link text-primary text-decoration-none p-0 border-0"
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                                const response = await api.get(`/crm/expenses/download/${row.id}`, {
                                    responseType: 'blob'
                                });
                                
                                // Extract original filename (remove timestamp prefix if exists)
                                let originalFileName = value;
                                if (originalFileName && originalFileName.includes("_")) {
                                    const lastUnderscoreIndex = originalFileName.lastIndexOf("_");
                                    if (lastUnderscoreIndex > 0) {
                                        originalFileName = originalFileName.substring(lastUnderscoreIndex + 1);
                                    }
                                }
                                
                                // Create blob URL and download the file
                                const blob = new Blob([response.data]);
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = originalFileName;
                                link.target = '_blank';
                                link.rel = 'noopener noreferrer';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                            } catch (error) {
                                console.error('Error downloading file:', error);
                                setAlert({
                                    type: 'danger',
                                    message: 'Failed to download file. Please try again.'
                                });
                            }
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file-type-pdf text-danger me-1" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
                            <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
                            <path d="M17 18h2" />
                            <path d="M20 15h-3v6" />
                            <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
                        </svg>
                        View
                    </button>
                );
            }
        },
        { 
            key: 'amount', 
            label: 'Amount',
            className: 'text-truncate',
            render: (value, row) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '150px', width: '150px' }}
                    title={formatCurrency(value, row.currencyCode)}
                >
                    {formatCurrency(value, row.currencyCode)}
                </span>
            )
        }
    ];

    // Invoice table columns (matching Invoice page structure except client name)
    const invoiceColumns = [
        {
            key: 'uploadedPdf',
            label: 'PDF',
            render: (value, item) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // TODO: Implement PDF preview functionality
                        alert('PDF preview will be implemented');
                    }}
                    title={item?.documentPath ? 'View PDF' : 'PDF not available'}
                >
                    <FileText className="icon-tabler icon-tabler-download" width="18" height="18" />
                </a>
            )
        },
        {
            key: 'invoiceNumber',
            label: 'Invoice Number',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '150px', width: '150px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'jurnalNumber',
            label: 'Jurnal Number',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '150px', width: '150px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'departmentName',
            label: 'Department',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '150px', width: '150px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'description',
            label: 'Description',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '250px', width: '250px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'projectName',
            label: 'Project',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '200px', width: '200px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'proposalCode',
            label: 'Order Number',
            className: 'text-truncate',
            render: (value, item) => {
                const orderNumber = item?.proposalCode || item?.proposal_code || '';
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '200px', width: '200px' }}
                        title={orderNumber}
                    >
                        {orderNumber || '-'}
                    </span>
                );
            }
        },
        {
            key: 'subtotal',
            label: 'Sub Total',
            className: 'text-truncate',
            render: (value, item) => {
                const currencyCode = item?.displayCurrencyCode || item?.currencyCode || 'IDR';
                const formattedValue = formatCurrency(value || 0, currencyCode);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            }
        },
        {
            key: 'ppn',
            label: 'PPN',
            className: 'text-truncate',
            render: (value, item) => {
                const currencyCode = item?.displayCurrencyCode || item?.currencyCode || 'IDR';
                const formattedValue = formatCurrency(value || 0, currencyCode);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            }
        },
        {
            key: 'totalInvoiced',
            label: 'Total Invoiced',
            className: 'text-truncate',
            render: (value, item) => {
                const currencyCode = item?.displayCurrencyCode || item?.currencyCode || 'IDR';
                const formattedValue = formatCurrency(value, currencyCode);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            }
        },
        {
            key: 'paymentReceived',
            label: 'Payment Received',
            className: 'text-truncate',
            render: (value, item) => {
                const currencyCode = item?.displayCurrencyCode || item?.currencyCode || 'IDR';
                const formattedValue = formatCurrency(value, currencyCode);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            }
        },
        {
            key: 'due',
            label: 'Due',
            className: 'text-truncate',
            render: (value, item) => {
                const currencyCode = item?.displayCurrencyCode || item?.currencyCode || 'IDR';
                const formattedValue = formatCurrency(value, currencyCode);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={`${formattedValue} - Total invoice amount due by due date`}
                    >
                        {formattedValue}
                    </span>
                );
            }
        },
        {
            key: 'balance',
            label: 'Balance',
            className: 'text-truncate',
            render: (value, item) => {
                const currencyCode = item?.displayCurrencyCode || item?.currencyCode || 'IDR';
                const formattedValue = formatCurrency(value, currencyCode);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={`${formattedValue} - Remaining amount after payments`}
                    >
                        {formattedValue}
                    </span>
                );
            }
        },
        {
            key: 'billDate',
            label: 'Bill Date',
            type: 'date',
            className: 'text-truncate',
            render: (value) => {
                const dateValue = formatDateToDDMMYYYY(value);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '140px', width: '140px' }}
                        title={dateValue}
                    >
                        {dateValue}
                    </span>
                );
            }
        },
        {
            key: 'dueDate',
            label: 'Due Date',
            type: 'date',
            className: 'text-truncate',
            render: (value) => {
                const dateValue = formatDateToDDMMYYYY(value);
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ maxWidth: '140px', width: '140px' }}
                        title={dateValue}
                    >
                        {dateValue}
                    </span>
                );
            }
        },
        {
            key: 'assignedUserName',
            label: 'Assigned To',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '160px', width: '160px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'createdByName',
            label: 'Created By',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '160px', width: '160px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'updatedByName',
            label: 'Updated By',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '160px', width: '160px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'serviceName',
            label: 'Service',
            className: 'text-truncate',
            render: (value) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '150px', width: '150px' }}
                    title={value}
                >
                    {value || '-'}
                </span>
            )
        },
        {
            key: 'status',
            label: 'Status',
            className: 'text-truncate',
            render: (value) => {
                const colors = {
                    'PAID': 'bg-success text-white',
                    'UNPAID': 'bg-warning text-white',
                    'OVERDUE': 'bg-danger text-white',
                    'PARTIAL': 'bg-info text-white',
                    'DRAFT': 'badge-outline text-orange',
                    'SENT': 'badge-outline text-blue',
                    'CANCELLED': 'badge-outline text-gray',
                    'FINAL': 'bg-primary text-white'
                };
                const statusText = value === 'SENT' ? 'SENT TO FINANCE' : value;
                return (
                    <span
                        className={`${colors[value] || 'badge-outline text-gray'} badge text-truncate d-inline-block`}
                        style={{ maxWidth: '150px', width: '150px' }}
                        title={statusText}
                    >
                        {statusText}
                    </span>
                );
            }
        }
    ];

    // Function to load files
    const loadFiles = async () => {
        console.log('loadFiles called with id:', id, 'realProjectId:', realProjectId);
        setLoadingFiles(true);
        try {
            // Use real project ID if available, otherwise use lead ID from URL
            const projectIdToUse = realProjectId || id;
            const allFiles = [];
            console.log('Using projectIdToUse:', projectIdToUse);

            // 1. Fetch project documents (if real project exists)
            if (realProjectId) {
                try {
                    const projectResponse = await api.get(`/crm/project-documents/project/${realProjectId}`);
                    const projectFiles = (projectResponse.data || []).map(file => ({
                        ...file,
                        source: 'Project',
                        sourceId: realProjectId,
                        downloadEndpoint: `/crm/project-documents/download/${file.id}`
                    }));
                    allFiles.push(...projectFiles);
                } catch (projectError) {
                    console.warn('Error loading project files:', projectError);
                    // Continue with other sources
                }
            }

            // 2. Fetch proposal documents (if proposal exists)
            try {
                const proposalResponse = await api.get(`/crm/proposals/detail/lead/${id}`);
                if (proposalResponse.data && proposalResponse.data.id) {
                    const proposalId = proposalResponse.data.id;
                    const proposalDocResponse = await api.get(`/crm/proposal-documents/proposal/${proposalId}`);
                    const proposalFiles = (proposalDocResponse.data || []).map(file => ({
                        ...file,
                        source: 'Proposal',
                        sourceId: proposalId,
                        downloadEndpoint: `/crm/proposal-documents/download/${file.id}`
                    }));
                    allFiles.push(...proposalFiles);
                }
            } catch (proposalError) {
                console.warn('Error loading proposal files:', proposalError);
                // Continue with other sources
            }

            // 3. Fetch lead service documents (if any exist)
            try {
                console.log('Fetching lead documents for lead ID:', id);
                const leadDocResponse = await api.get(`/crm/leads/${id}/documents`);
                console.log('Lead documents response:', leadDocResponse.data);
                console.log('Lead documents response status:', leadDocResponse.status);

                if (leadDocResponse.data && Array.isArray(leadDocResponse.data)) {
                    const leadFiles = leadDocResponse.data.map(file => ({
                        ...file,
                        source: 'Lead',
                        sourceId: id,
                        downloadEndpoint: file.url // Use the URL field provided by backend
                    }));

                    console.log('Processed lead files:', leadFiles);
                    allFiles.push(...leadFiles);
                } else {
                    console.log('No lead documents found or invalid response format');
                }
            } catch (leadError) {
                console.warn('Error fetching lead documents:', leadError);
                console.warn('Error details:', leadError.response?.data || leadError.message);
                // Continue - this endpoint might not exist yet
            }

            // 4. Fetch invoice documents related to this project
            try {
                const invoiceResponse = await api.get(`/crm/invoices/project/${projectIdToUse}`);
                const invoices = invoiceResponse.data || [];

                // For each invoice, if it has a document path, add it to files
                for (const invoice of invoices) {
                    if (invoice.documentPath && invoice.documentPath.trim()) {
                        const invoiceFile = {
                            id: `invoice-${invoice.id}`,
                            fileName: invoice.documentPath,
                            originalFileName: invoice.documentPath,
                            fileSize: null, // Size not available from invoice
                            fileType: invoice.documentPath.toLowerCase().endsWith('.pdf') ? '.pdf' : '',
                            mimeType: 'application/pdf',
                            description: `Invoice ${invoice.invoiceNumber || invoice.id}`,
                            documentType: 'invoice',
                            createdAt: invoice.createdAt || invoice.created_at,
                            updatedAt: invoice.updatedAt || invoice.updated_at,
                            uploadedBy: invoice.createdBy || 'System',
                            source: 'Invoice',
                            sourceId: invoice.id,
                            downloadEndpoint: `/crm/invoices/${invoice.id}/download`
                        };
                        allFiles.push(invoiceFile);
                    }
                }
            } catch (invoiceError) {
                console.warn('Error loading invoice files:', invoiceError);
                // Continue with other sources
            }

            // Sort files by creation date (newest first)
            allFiles.sort((a, b) => {
                const dateA = new Date(a.createdAt || a.uploadDate || a.created_at || 0);
                const dateB = new Date(b.createdAt || b.uploadDate || b.created_at || 0);
                return dateB - dateA;
            });

            setFiles(allFiles);
        } catch (error) {
            console.error('Error loading files:', error);
            console.error('Error details:', error.response);
            setAlert({
                type: 'danger',
                message: 'Failed to load files: ' + (error.response?.data?.message || error.message)
            });
        } finally {
            setLoadingFiles(false);
        }
    };

    // Function to download file
    const handleDownloadFile = async (file) => {
        try {
            // Use the download endpoint/URL provided by the file object
            const downloadUrl = file.url || file.downloadEndpoint || `/crm/project-documents/download/${file.id}`;

            const response = await api.get(downloadUrl, {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.fileName || file.originalFileName || file.name || 'file');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setAlert({
                type: 'success',
                message: `File "${file.fileName || file.originalFileName}" downloaded successfully!`
            });
        } catch (error) {
            console.error('Error downloading file:', error);
            setAlert({
                type: 'danger',
                message: 'Failed to download file: ' + (error.response?.data?.message || error.message)
            });
        }
    };

    // Function to delete file
    const handleDeleteFile = (file) => {
        setFileToDelete(file);
        setShowDeleteFileModal(true);
    };

    // Function to confirm delete file
    const confirmDeleteFile = async () => {
        if (!fileToDelete) return;

        try {
            // Use the appropriate delete endpoint based on file source
            let deleteUrl;
            if (fileToDelete.source === 'Project') {
                deleteUrl = `/crm/project-documents/${fileToDelete.id}`;
            } else if (fileToDelete.source === 'Proposal') {
                deleteUrl = `/crm/proposal-documents/${fileToDelete.id}`;
            } else if (fileToDelete.source === 'Lead') {
                // Lead files cannot be deleted from project detail page
                setAlert({
                    type: 'warning',
                    message: 'Lead files cannot be deleted from project detail page. Please manage them from the lead page.'
                });
                setShowDeleteFileModal(false);
                setFileToDelete(null);
                return;
            } else if (fileToDelete.source === 'Invoice') {
                // Invoice files cannot be deleted
                setAlert({
                    type: 'warning',
                    message: 'Invoice files cannot be deleted as they are system-generated documents.'
                });
                setShowDeleteFileModal(false);
                setFileToDelete(null);
                return;
            } else {
                // Fallback to project documents
                deleteUrl = `/crm/project-documents/${fileToDelete.id}`;
            }

            await api.delete(deleteUrl);
            setAlert({
                type: 'success',
                message: `File "${fileToDelete.fileName || fileToDelete.originalFileName}" deleted successfully!`
            });
            // Reload files list to reflect the deletion
            await loadFiles();
        } catch (error) {
            console.error('Error deleting file:', error);
            setAlert({
                type: 'danger',
                message: 'Failed to delete file: ' + (error.response?.data?.message || error.message)
            });
        } finally {
            setShowDeleteFileModal(false);
            setFileToDelete(null);
        }
    };

    // Function to upload file
    const handleUploadFile = async (file, description = '', documentType = 'attachment') => {
        setUploadingFile(true);
        try {
            // Use real project ID if available, otherwise use lead ID from URL
            const projectIdToUse = realProjectId || id;
            
            const formData = new FormData();
            formData.append('file', file);
            if (description) {
                formData.append('description', description);
            }
            formData.append('documentType', documentType);
            const response = await api.post(`crm/project-documents/project/${projectIdToUse}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setAlert({
                type: 'success',
                message: `File "${file.name}" uploaded successfully!`
            });
            // Reload files list to show the new file
            await loadFiles();
        } catch (error) {
            console.error('Error uploading file:', error);
            setAlert({
                type: 'danger',
                message: 'Failed to upload file: ' + (error.response?.data?.message || error.message)
            });
        } finally {
            setUploadingFile(false);
        }
    };

    // File table columns configuration
    const fileColumns = [
        {
            key: 'fileName',
            label: 'File Name',
            render: (fileName, file) => (
                <div className="d-flex align-items-center">
                    {getFileIcon(file.fileName || file.originalFileName || file.name)}
                    <span className="fw-bold">{file.fileName || file.originalFileName || file.name || 'Unknown File'}</span>
                </div>
            ),
            sortable: true
        },
        {
            key: 'source',
            label: 'Source',
            render: (source, file) => (
                <span className={`badge ${
                    file.source === 'Project' ? 'bg-primary text-white' :
                    file.source === 'Proposal' ? 'bg-success text-white' :
                    file.source === 'Lead' ? 'bg-info text-white' :
                    file.source === 'Invoice' ? 'bg-warning text-white' :
                    'bg-secondary'
                }`}>
                    {source || 'Unknown'}
                </span>
            ),
            sortable: true
        },
        {
            key: 'fileSize',
            label: 'File Size',
            render: (fileSize, file) => (
                <span className="text-muted">{formatFileSize(fileSize || file.fileSize || file.size || 0)}</span>
            ),
            sortable: true
        },
        // {
        //     key: 'uploadedBy',
        //     label: 'Uploaded By',
        //     render: (uploadedBy, file) => (
        //         <div>
        //             <div className="fw-bold">{uploadedBy || file.uploadedBy || file.uploaded_by || file.createdBy || 'Unknown User'}</div>
        //         </div>
        //     ),
        //     sortable: true
        // },
        {
            key: 'uploadDate',
            label: 'Upload Date',
            render: (uploadDate, file) => (
                <span className="text-muted">{formatDate(uploadDate || file.uploadDate || file.upload_date || file.createdAt)}</span>
            ),
            type: 'date',
            sortable: true
        }
    ];

    return (
        <>
            {alert && (
                <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
            )}
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="d-flex align-items-center">
                                <div className="d-flex align-items-center">
                                    <a className="btn btn-outline-white d-none d-sm-inline-block me-3" onClick={handleBack}>
                                        <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                                        Back
                                    </a>
                                    <h3 className="page-title mb-0">Project Details</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row align-items-center">
                    <div className="col-md-8 mt-3">
                        <p className="mb-2">
                            <span className="text-muted fw-normal">Company Name: </span>
                            {companyId ? (
                              <a
                                href={`/clients/${companyId}`}
                                className="text-primary text-decoration-none fw-bold"
                                onClick={(e) => {
                                  // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                                  if (e.ctrlKey || e.metaKey || e.button === 1) {
                                    return; // Let browser handle it
                                  }
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigate(`/clients/${companyId}`);
                                }}
                                onAuxClick={(e) => {
                                  // Handle middle mouse button click
                                  if (e.button === 1) {
                                    return; // Let browser handle it
                                  }
                                }}
                                title={`View client details for ${projectData.client.name}`}
                                aria-label={`View client details for ${projectData.client.name}`}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate(`/clients/${companyId}`);
                                  }
                                }}
                              >
                                {projectData.client.name}
                              </a>
                            ) : (
                              <span className="text-dark fw-bold">{projectData.client.name}</span>
                            )}
                        </p>
                        {leadData && (
                            <p className="mb-2">
                                <span className="text-muted fw-normal">Project Name: </span>
                                <a
                                    href={`/leads/detail/${id}`}
                                    className="text-primary text-decoration-none fw-bold"
                                    onClick={(e) => {
                                        // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                                            return; // Let browser handle it
                                        }
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNavigateToLeadDetail(id);
                                    }}
                                    onAuxClick={(e) => {
                                        // Handle middle mouse button click
                                        if (e.button === 1) {
                                            return; // Let browser handle it
                                        }
                                    }}
                                    title={`View lead details for ${leadData.projectName}`}
                                    aria-label={`View lead details for ${leadData.projectName}`}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleNavigateToLeadDetail(id);
                                        }
                                    }}
                                >
                                    {leadData.projectName || 'Project Name'}
                                </a>
                            </p>
                        )}
                        {projectData.proposal_number && (
                            <p className="mb-2">
                                <span className="text-muted fw-normal">Proposal Number: </span>
                                <a
                                    href={`/proposals/detail/${id}`}
                                    className="text-primary text-decoration-none fw-bold"
                                    onClick={(e) => {
                                        // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                                        if (e.ctrlKey || e.metaKey || e.button === 1) {
                                            return; // Let browser handle it
                                        }
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNavigateToProposal(id);
                                    }}
                                    onAuxClick={(e) => {
                                        // Handle middle mouse button click
                                        if (e.button === 1) {
                                            return; // Let browser handle it
                                        }
                                    }}
                                    title={`View proposal details for ${projectData.proposal_number}`}
                                    aria-label={`View proposal details for ${projectData.proposal_number}`}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleNavigateToProposal(id);
                                        }
                                    }}
                                >
                                    {projectData.proposal_number}
                                </a>
                            </p>
                        )}
                        <p className="mb-2">
                            <span className="text-muted fw-normal">Project Number: </span>
                            <span className="text-dark fw-bold">{projectData.project_number}</span>
                        </p>
                    </div>
                </div>

                {/* Document Preview Card */}
                {(projectData.projectPdfFill || documentFile) && (
                    <div className="card mb-3 border-0 shadow-sm" style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>
                        <div
                            className="card-header border-0 d-flex align-items-center"
                            style={{ background: 'linear-gradient(135deg, #0d3d42 0%, #1f656c 55%, #2a8a93 100%)', color: '#fff', padding: '0.75rem 1.25rem' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M14 2H6a2 2 0 0 0 -2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2V8z"/>
                                <polyline points="14,2 14,8 20,8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                                <polyline points="10,9 9,9 8,9"/>
                            </svg>
                            <h5 className="card-title mb-0 text-white" style={{ fontWeight: 600 }}>Pre-fill Document</h5>
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1f656c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M14 2H6a2 2 0 0 0 -2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2V8z"/>
                                        <polyline points="14,2 14,8 20,8"/>
                                    </svg>
                                    <div>
                                        <div className="fw-semibold">{documentFile ? documentFile.name : 'Pre-fill Document.pdf'}</div>
                                        <small className="text-muted">PDF Document</small>
                                    </div>
                                </div>
                                <div className="btn-group">
                                    <button className="btn btn-sm btn-outline-teal" onClick={handleDocumentPreview} title="Preview document">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                            <path d="M1 12s4 -8 11 -8 11 8 11 8 -4 8 -11 8 -11 -8 -11 -8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                        </svg>
                                        Preview
                                    </button>
                                    <label className="btn btn-sm btn-outline-secondary" title="Upload new document">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                            <path d="M21 15v4a2 2 0 0 1 -2 2h-8l-4 4l-4 -4h-8a2 2 0 0 1 -2 -2v-4"/>
                                            <polyline points="17,8 12,3 7,8"/>
                                            <line x1="12" y1="3" x2="12" y2="15"/>
                                        </svg>
                                        Upload
                                        <input type="file" accept="application/pdf" onChange={handleDocumentUpload} style={{ display: 'none' }} />
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="page-body mt-3">
                    <div className="row">
                        <div className="col-md-8">
                            <div className="card">


                                <div className="card-header">
                                    <ul className="nav nav-tabs card-header-tabs" role="tablist">
                                        <li className="nav-item-tab" role="presentation">
                                            <a
                                                href="#tabs-project-item"
                                                className={`nav-link ${activeTab === 'tabs-project-item' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleTabChange('tabs-project-item');
                                                }}
                                                aria-selected={activeTab === 'tabs-project-item'}
                                                role="tab"
                                            >
                                                Project Item
                                            </a>
                                        </li>
                                        <li className="nav-item-tab" role="presentation">
                                            <a
                                                href="#tabs-files"
                                                className={`nav-link ${activeTab === 'tabs-files' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleTabChange('tabs-files');
                                                }}
                                                aria-selected={activeTab === 'tabs-files'}
                                                role="tab"
                                            >
                                                Files
                                            </a>
                                        </li>
                                        <li className="nav-item-tab" role="presentation">
                                            <a
                                                href="#tabs-expense"
                                                className={`nav-link ${activeTab === 'tabs-expense' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleTabChange('tabs-expense');
                                                }}
                                                aria-selected={activeTab === 'tabs-expense'}
                                                role="tab"
                                            >
                                                Expense
                                            </a>
                                        </li>
                                        <li className="nav-item-tab" role="presentation">
                                            <a
                                                href="#tabs-invoice"
                                                className={`nav-link ${activeTab === 'tabs-invoice' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleTabChange('tabs-invoice');
                                                }}
                                                aria-selected={activeTab === 'tabs-invoice'}
                                                role="tab"
                                            >
                                                Invoice
                                            </a>
                                        </li>
                                        <li className="nav-item-tab" role="presentation">
                                            <a
                                                href="#tabs-investment-breakdown"
                                                className={`nav-link ${activeTab === 'tabs-investment-breakdown' ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleTabChange('tabs-investment-breakdown');
                                                }}
                                                aria-selected={activeTab === 'tabs-investment-breakdown'}
                                                role="tab"
                                            >
                                                Investment Breakdown
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                                <div className="card-body">
                                    <div className="tab-content">
                                        <div className={`tab-pane ${activeTab === 'tabs-project-item' ? 'active show' : ''}`} id="tabs-project-item" role="tabpanel">
                                            {/* Audit Methodology summary (carried from the proposal's Audit Methodology section) */}
                                            <div className="card mb-3 border-0 shadow-sm" style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>
                                                <div
                                                    className="card-header border-0 d-flex align-items-center"
                                                    style={{ background: 'linear-gradient(135deg, #0d3d42 0%, #1f656c 55%, #2a8a93 100%)', color: '#fff', padding: '0.85rem 1.25rem' }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2"/><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z"/><path d="M9 14l2 2l4 -4"/></svg>
                                                    <h5 className="card-title mb-0 text-white" style={{ fontWeight: 600 }}>Audit Methodology Summary</h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-6 col-md-3">
                                                            <div className="text-muted small">Methodology</div>
                                                            <div className="fw-bold">{projectData.auditMethodology || auditFlagLabel}</div>
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <div className="text-muted small">Sites</div>
                                                            <div className="fw-bold">{projectData.multiSite ? 'Multi-site' : 'Single site'}</div>
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <div className="text-muted small">Audit standards</div>
                                                            <div className="fw-bold">{auditReadiness.rootStandardCount}</div>
                                                        </div>
                                                        <div className="col-12 col-md-3">
                                                            <div className="text-muted small">
                                                                Audit Platform status
                                                                {auditReadiness.waitingForAuditDate && (
                                                                    <span className="ms-1" title="Click on a service row below to edit its Audit Date" style={{ cursor: 'help' }}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1f656c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                                            <circle cx="12" cy="12" r="10"/>
                                                                            <line x1="12" y1="16" x2="12" y2="12"/>
                                                                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                                                                        </svg>
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className={`badge ${auditReadiness.waitingForAuditDate ? 'bg-danger text-white' : auditReadiness.ready ? 'bg-success text-white' : 'bg-warning text-dark'}`}>
                                                                {auditReadiness.waitingForAuditDate ? 'Waiting for Audit Date' : auditReadiness.ready ? 'Ready' : 'In progress'}
                                                            </span>
                                                            {auditReadiness.waitingForAuditDate && (
                                                                <div className="mt-1">
                                                                    <small className="text-danger" style={{ fontSize: '0.75rem' }}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                                            <circle cx="12" cy="12" r="10"/>
                                                                            <line x1="12" y1="8" x2="12" y2="12"/>
                                                                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                                                                        </svg>
                                                                        Click service rows below to add Audit Date
                                                                    </small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {auditDateAwareness?.handlers?.length > 0 && (
                                                        <div
                                                            className="mt-3 p-3"
                                                            style={{
                                                                border: '1px solid rgba(31, 101, 108, 0.18)',
                                                                borderLeft: '4px solid #1f656c',
                                                                borderRadius: '0.5rem',
                                                                background: 'linear-gradient(90deg, rgba(31,101,108,0.08), rgba(42,138,147,0.03))'
                                                            }}
                                                            aria-live="polite"
                                                        >
                                                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 30, height: 30, background: '#1f656c', color: '#fff' }}>
                                                                        <ClipboardCheck size={16} aria-hidden="true" />
                                                                    </span>
                                                                    <div>
                                                                        <div className="fw-bold" style={{ color: '#0d3d42' }}>Audit Process Handoff</div>
                                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                                            <div className="text-muted small">Current step: {auditDateAwareness.currentStep}</div>
                                                                            {auditDateAwareness.locked && (
                                                                                <span className="badge bg-warning text-dark d-inline-flex align-items-center gap-1">
                                                                                    <Lock size={12} aria-hidden="true" />
                                                                                    Date managed in Scheduling
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                                                    onClick={() => navigate(realProjectId ? `/audit-process/${realProjectId}` : `/audit-process/${projectData?.id || id}`)}
                                                                >
                                                                    <UsersRound size={14} aria-hidden="true" />
                                                                    Open Audit Process
                                                                </button>
                                                            </div>
                                                            <div className="row g-2">
                                                                {auditDateAwareness.handlers.map((handler, idx) => (
                                                                    <div className="col-12 col-lg-6" key={`${handler.step || 'audit-handler'}-${handler.taskId || idx}`}>
                                                                        <div className="d-flex align-items-start justify-content-between gap-2 p-2 bg-white border rounded-2 h-100">
                                                                            <div>
                                                                                <div className="small text-muted">{handler.step || 'Audit task'}</div>
                                                                                <div className="fw-semibold">{handler.assigneeName || handler.assigneeEmail || 'Assignee resolving'}</div>
                                                                                {handler.assigneeEmail && handler.assigneeEmail !== handler.assigneeName && (
                                                                                    <div className="text-muted small">{handler.assigneeEmail}</div>
                                                                                )}
                                                                            </div>
                                                                            <span className={`badge ${String(handler.status || '').toUpperCase() === 'DONE' ? 'bg-success text-white' : 'bg-info text-dark'}`}>
                                                                                {handler.status || 'OPEN'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Project Date Inputs */}
                                            <div className="card mb-3">
                                                <div className="card-body">
                                                    <h5 className="card-title">Project Timeline</h5>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-bold">Project Start Date</label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                value={projectStartDate}
                                                                onChange={handleProjectStartDateChange}
                                                                placeholder="Select start date"
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label className="form-label fw-bold">Project End Date</label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                value={projectEndDate}
                                                                onChange={handleProjectEndDateChange}
                                                                placeholder="Select end date"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {projectData.project_sections.map((section, index) => (
                                                <div key={index}>
                                                    {renderServiceTable(
                                                        section.services,
                                                        index === 0 ? 'Services' : 'Additional Services',
                                                        section.type
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <div className={`tab-pane ${activeTab === 'tabs-files' ? 'active show' : ''}`} id="tabs-files" role="tabpanel">
                                            {/* File Upload Section */}
                                            <div className="mb-4">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <h3 className="mb-0">Files</h3>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="file"
                                                            id="file-upload"
                                                            className="d-none"
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) {
                                                                    handleUploadFile(file);
                                                                    e.target.value = ''; // Reset input
                                                                }
                                                            }}
                                                            disabled={uploadingFile}
                                                        />
                                                        <label
                                                            htmlFor="file-upload"
                                                            className={`btn btn-primary ${uploadingFile ? 'disabled' : ''}`}
                                                            style={{ cursor: uploadingFile ? 'not-allowed' : 'pointer' }}
                                                        >
                                                            {uploadingFile ? (
                                                                <>
                                                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                                                        <span className="visually-hidden">Uploading...</span>
                                                                    </div>
                                                                    Uploading...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-upload me-2" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
                                                                        <path d="M7 9l5 -5l5 5"></path>
                                                                        <path d="M12 4l0 12"></path>
                                                                    </svg>
                                                                    Upload File
                                                                </>
                                                            )}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Files Table */}
                                            <GenericView
                                                data={files}
                                                columns={fileColumns}
                                                onEdit={undefined}
                                                onDelete={handleDeleteFile}
                                                additionalActions={(file) => (
                                                    <div className="d-flex align-items-center">
                                                        <a
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleDownloadFile(file);
                                                            }}
                                                            title="Download file"
                                                        >
                                                            <ArrowDownToLine className="icon icon-tabler icon-tabler-download text-primary" />
                                                        </a>
                                                    </div>
                                                )}
                                                checkboxSelection={false}
                                                isLoading={loadingFiles}
                                                emptyStateMessage="No files uploaded yet"
                                                tableClassName="table card-table table-vcenter text-nowrap datatable table-hover"
                                                showTopControls={false}
                                                showPagination={true}
                                                initialPageSize={10}
                                            />
                                        </div>

                                        <div className={`tab-pane ${activeTab === 'tabs-expense' ? 'active show' : ''}`} id="tabs-expense" role="tabpanel">
                                            {/* Expense Section */}
                                            <div className="mb-4">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <h3 className="mb-0">Expenses</h3>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <button
                                                            className="btn btn-primary"
                                                            onClick={() => {
                                                                setSelectedExpense(null);
                                                                setIsExpenseModalOpen(true);
                                                            }}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus me-2" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                                <path d="M12 5l0 14"></path>
                                                                <path d="M5 12l14 0"></path>
                                                            </svg>
                                                            Add Expense
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Expense Table */}
                                            <GenericView
                                                data={expenses}
                                                columns={expenseColumns}
                                                onEdit={handleExpenseEdit}
                                                onDelete={handleExpenseDelete}
                                                checkboxSelection={false}
                                                isLoading={loadingExpenses}
                                                emptyStateMessage="No expenses found"
                                                tableClassName="table card-table table-vcenter text-nowrap datatable table-hover"
                                                showTopControls={false}
                                                showPagination={true}
                                                initialPageSize={10}
                                            />
                                        </div>

                                        <div className={`tab-pane ${activeTab === 'tabs-invoice' ? 'active show' : ''}`} id="tabs-invoice" role="tabpanel">
                                            {/* Invoice Section */}
                                            <div className="mb-4">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <h3 className="mb-0">Invoices</h3>
                                                </div>
                                            </div>
                                            {/* Invoice Table */}
                                            <GenericView
                                                data={invoices}
                                                columns={invoiceColumns}
                                                checkboxSelection={false}
                                                isLoading={loadingInvoices}
                                                emptyStateMessage="No invoices found"
                                                tableClassName="table card-table table-vcenter text-nowrap datatable table-hover"
                                                showTopControls={false}
                                                showPagination={true}
                                                initialPageSize={10}
                                            />
                                        </div>

                                        <div className={`tab-pane ${activeTab === 'tabs-investment-breakdown' ? 'active show' : ''}`} id="tabs-investment-breakdown" role="tabpanel">
                                            <div className="card w-100">
                                                <div className="card-header">
                                                    <h5 className="card-title mb-0">
                                                        Investment Breakdown
                                                        {investmentBreakdown && ` (${investmentBreakdown.years.length} Years)`}
                                                    </h5>
                                                </div>
                                                <div className="card-body">
                                                    {loadingBreakdown ? (
                                                        <div className="text-center py-4">
                                                            <div className="spinner-border text-primary" role="status"></div>
                                                        </div>
                                                    ) : investmentBreakdown ? (
                                                        <div className="table-responsive">
                                                            <table className="table table-bordered">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Details</th>
                                                                        <th style={{ width: '60px', minWidth: '60px' }}>Qty</th>
                                                                        <th style={{ width: '80px', minWidth: '80px' }}>Unit</th>
                                                                        {investmentBreakdown.years.map(year => (
                                                                            <th key={year}>{year}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {investmentBreakdown.breakdown.map((serviceRow, sIdx) => (
                                                                        <React.Fragment key={sIdx}>
                                                                            <tr>
                                                                                <td className="fw-bold" colSpan={3 + investmentBreakdown.years.length}>{serviceRow.service}</td>
                                                                            </tr>
                                                                            {serviceRow.standards.map((stdRow, stdIdx) => (
                                                                                <React.Fragment key={stdIdx}>
                                                                                    <tr>
                                                                                        <td style={{ paddingLeft: 24, fontWeight: 'bold' }} colSpan={3 + investmentBreakdown.years.length}>
                                                                                            {stdRow.standard}
                                                                                            {stdRow.version && stdRow.version.trim() !== '' && `:${stdRow.version}`}
                                                                                            {stdRow.standard_scheme && stdRow.standard_scheme.trim() !== '' && ` (${stdRow.standard_scheme})`}
                                                                                        </td>
                                                                                    </tr>
                                                                                    {stdRow.scope && stdRow.scope.trim() !== '' && (
                                                                                        <tr>
                                                                                            <td style={{ paddingLeft: 24 }} colSpan={3 + investmentBreakdown.years.length}>
                                                                                                <span className="text-muted small">Scope: {stdRow.scope}</span>
                                                                                            </td>
                                                                                        </tr>
                                                                                    )}
                                                                                    {stdRow.serviceItems.map((item, cidx) => (
                                                                                        <tr key={cidx}>
                                                                                            <td style={{ paddingLeft: 48 }}>
                                                                                                {item.serviceItem}
                                                                                            </td>
                                                                                            <td style={{ width: '60px', minWidth: '60px' }}>
                                                                                                {item.qty != null ? (() => {
                                                                                                    const qtyNum = parseFloat(item.qty);
                                                                                                    if (qtyNum % 1 === 0) {
                                                                                                        return qtyNum.toString();
                                                                                                    }
                                                                                                    return qtyNum.toString().replace(/\.?0+$/, '');
                                                                                                })() : '-'}
                                                                                            </td>
                                                                                            <td style={{ width: '80px', minWidth: '80px' }}>{item.unit || '-'}</td>
                                                                                            {investmentBreakdown.years.map((year) => {
                                                                                                const yearIdx = item.years.indexOf(year);
                                                                                                return (
                                                                                                    <td key={year}>
                                                                                                        {yearIdx !== -1
                                                                                                            ? (item.currencyCode && item.currencyCode.toUpperCase() !== 'IDR'
                                                                                                                ? `${getCurrencyPrefix(item.currencyCode)}${Number(item.unitPrice || 0).toLocaleString('id-ID')}${item.qty != null && Number(item.qty) !== 1 ? ` × ${Number(item.qty).toLocaleString('id-ID')}` : ''} × ${Number(item.exchangeRate || 1).toLocaleString('id-ID')} = Rp ${Number(item.prices[yearIdx] || 0).toLocaleString('id-ID')}`
                                                                                                                : getCurrencyPrefix(proposalCurrencyCode) + Number(item.prices[yearIdx] || 0).toLocaleString('id-ID'))
                                                                                                            : '-'}
                                                                                                    </td>
                                                                                                );
                                                                                            })}
                                                                                        </tr>
                                                                                    ))}
                                                                                </React.Fragment>
                                                                            ))}
                                                                        </React.Fragment>
                                                                    ))}
                                                                </tbody>
                                                                <tfoot>
                                                                    <tr>
                                                                        <th>Total</th>
                                                                        <th></th>
                                                                        <th></th>
                                                                        {investmentBreakdown.totals.map((total, i) => (
                                                                            <th key={i}>{getCurrencyPrefix(proposalCurrencyCode) + total.toLocaleString('id-ID')}</th>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <th>Grand Total</th>
                                                                        <th colSpan={Math.max(investmentBreakdown.totals.length, 1) + 2} className="text-end">
                                                                            {getCurrencyPrefix(proposalCurrencyCode) + investmentBreakdown.totals.reduce((sum, t) => sum + t, 0).toLocaleString('id-ID')}
                                                                        </th>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <h5 className="text-muted">Investment Breakdown</h5>
                                                            <p className="text-muted mb-0">
                                                                No investment breakdown data is available for this project's proposal.
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-body">
                                    <h2 className="card-title">Info</h2>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Client</div>
                                        <div className='datagrid-content'>
                                          {companyId ? (
                                            <a
                                              href={`/clients/${companyId}`}
                                              className="text-primary text-decoration-none"
                                              onClick={(e) => {
                                                // Allow right-click, Ctrl+Click, Cmd+Click to open in new tab
                                                if (e.ctrlKey || e.metaKey || e.button === 1) {
                                                  return; // Let browser handle it
                                                }
                                                e.preventDefault();
                                                e.stopPropagation();
                                                navigate(`/clients/${companyId}`);
                                              }}
                                              onAuxClick={(e) => {
                                                // Handle middle mouse button click
                                                if (e.button === 1) {
                                                  return; // Let browser handle it
                                                }
                                              }}
                                              title={`View client details for ${projectData.client.name}`}
                                              aria-label={`View client details for ${projectData.client.name}`}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  navigate(`/clients/${companyId}`);
                                                }
                                              }}
                                            >
                                              {projectData.client.name}
                                            </a>
                                          ) : (
                                            <span>{projectData.client.name}</span>
                                          )}
                                        </div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Project Status</div>
                                        <div className='badge badge-outline text-primary'>{projectData.statusProject}</div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Project Year</div>
                                        <div className='datagrid-content'>
                                            {calculateRemainingYears(projectData.project_sections)} year{calculateRemainingYears(projectData.project_sections) !== 1 ? 's' : ''} left
                                        </div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Services</div>
                                        <div>
                                            {projectData.project_sections.map(section =>
                                                section.services.map(service => (
                                                    <div key={`${section.id}-${service.standard}`} className='badge bg-primary text-white me-2 mb-1'>
                                                        {service.standard || 'Service'}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Project Start Date</div>
                                        <div className='datagrid-content'>{formatDateToDDMMYYYY(projectData.dates.project_date)}</div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Project End Date</div>
                                        <div className='datagrid-content'>{formatDateToDDMMYYYY(projectData.dates.valid_until)}</div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Assigned To</div>
                                        <div className='datagrid-content'>{projectData.assigned_to.name}</div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Department</div>
                                        <div className='datagrid-content'>{projectData.assigned_to.team || '-'}</div>
                                    </div>
                                    <div className='mb-3'>
                                        <div className='datagrid-title'>Contact</div>
                                        <div className='datagrid-content'>{projectData.client.contact_person}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="card mt-3">
                                <div className="card-body">
                                    <h2 className="card-title">Project History</h2>
                                    <div className="activity-timeline">
                                        {loadingHistory ? (
                                            <div className="text-center py-3">
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <div className="text-muted mt-2">Loading history...</div>
                                            </div>
                                        ) : projectHistory && projectHistory.length > 0 ? (
                                            projectHistory.map((activity, index) => (
                                                    <div key={activity.id || index} className="activity-item">
                                                        <div className="row g-0">
                                                            <div className="col-auto position-relative">
                                                                <div className={`activity-icon bg-${getActivityColor(activity.type)} text-white rounded-circle d-flex align-items-center justify-content-center`}
                                                                    style={{
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        position: 'relative',
                                                                        zIndex: 2
                                                                    }}>
                                                                    {getActivityIcon(activity.type)}
                                                                </div>
                                                            </div>
                                                            <div className="col ms-3">
                                                                <div>
                                                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                                                        <div className={`activity-title text-${getActivityColor(activity.type)}`}>
                                                                            {getActivityTitle(activity.type)}
                                                                        </div>
                                                                        <div className="activity-time text-muted small">
                                                                            {activity.date}
                                                                        </div>
                                                                    </div>
                                                                    <div className="activity-description text-muted">
                                                                        <span className="fw-bold">{activity.user}</span> {activity.notes}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                        ) : (
                                            <div className="text-center text-muted py-3">No project history</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete File Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteFileModal}
                onClose={() => setShowDeleteFileModal(false)}
                onConfirm={confirmDeleteFile}
                title="Delete File"
                message={`Are you sure you want to delete "${fileToDelete?.fileName || fileToDelete?.originalFileName}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmButtonClass="btn-danger"
            />

            {/* Expense Form Modal */}
            <ExpenseForm
                isOpen={isExpenseModalOpen}
                onClose={() => {
                    setIsExpenseModalOpen(false);
                    setSelectedExpense(null);
                }}
                onSubmit={handleExpenseSubmit}
                initialData={selectedExpense || (companyId && realProjectId ? {
                    companyId: companyId,
                    projectId: realProjectId
                } : null)}
                disabledClientId={companyId}
                disabledProjectId={realProjectId}
            />

            {/* Document Preview/Upload Modal */}
            <Modal
                isOpen={isDocumentModalOpen}
                onClose={closeDocumentModal}
                title="Document Preview"
                size="lg"
            >
                <div className="document-modal-content">
                    {documentPreview ? (
                        <div className="mb-3">
                            <iframe
                                src={documentPreview}
                                title="Document Preview"
                                style={{ width: '100%', height: '500px', border: 'none' }}
                            />
                        </div>
                    ) : (
                        <div className="text-center text-muted py-5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-2">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M14 2H6a2 2 0 0 0 -2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2V8z"/>
                                <polyline points="14,2 14,8 20,8"/>
                            </svg>
                            <p>No document to preview</p>
                        </div>
                    )}
                    <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
                        <label className="btn btn-outline-secondary btn-sm mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M21 15v4a2 2 0 0 1 -2 2h-8l-4 4l-4 -4h-8a2 2 0 0 1 -2 -2v-4"/>
                                <polyline points="17,8 12,3 7,8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            Upload PDF
                            <input type="file" accept="application/pdf" onChange={handleDocumentUpload} style={{ display: 'none' }} />
                        </label>
                        <div className="d-flex gap-2">
                            <button className="btn btn-outline-secondary btn-sm" onClick={closeDocumentModal}>Cancel</button>
                            {documentFile && (
                                <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #0d3d42 0%, #1f656c 55%, #2a8a93 100%)', color: '#fff' }} onClick={saveDocument} disabled={uploadingDocument}>
                                    {uploadingDocument ? 'Uploading...' : 'Save Document'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

const getActivityIcon = (type) => {
    switch (type) {
        case 'creation':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                </svg>
            );
        case 'sent':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-send" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M10 14l11 -11"></path>
                    <path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5"></path>
                </svg>
            );
        case 'status_change':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-refresh" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"></path>
                    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"></path>
                </svg>
            );
        case 'revision':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-edit" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                    <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                    <path d="M16 5l3 3"></path>
                </svg>
            );
        case 'viewed':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-eye" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path>
                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6"></path>
                </svg>
            );
        case 'downloaded':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-download" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
                    <path d="M7 11l5 5l5 -5"></path>
                    <path d="M12 4l0 12"></path>
                </svg>
            );
        case 'rejected':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M18 6l-12 12"></path>
                    <path d="M6 6l12 12"></path>
                </svg>
            );
        case 'approved':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-check" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M5 12l5 5l10 -10"></path>
                </svg>
            );
        case 'commented':
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-message-circle" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M3 20l1.3 -3.9a9 8 0 1 1 3.4 2.9l-4.7 1"></path>
                </svg>
            );
        default:
            return (
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-circle" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"></path>
                </svg>
            );
    }
};

const getActivityTitle = (type) => {
    switch (type) {
        case 'creation':
            return 'Project Created';
        case 'sent':
            return 'Project Sent';
        case 'status_change':
            return 'Status Changed';
        case 'revision':
            return 'Project Revised';
        case 'viewed':
            return 'Project Viewed';
        case 'downloaded':
            return 'Project Downloaded';
        case 'rejected':
            return 'Project Rejected';
        case 'approved':
            return 'Project Approved';
        case 'commented':
            return 'Comment Added';
        default:
            return 'Activity';
    }
};

const getActivityColor = (type) => {
    switch (type) {
        case 'creation':
            return 'primary';
        case 'sent':
            return 'info';
        case 'status_change':
            return 'warning';
        case 'revision':
            return 'success';
        case 'viewed':
            return 'secondary';
        case 'downloaded':
            return 'dark';
        case 'rejected':
            return 'danger';
        case 'approved':
            return 'success';
        case 'commented':
            return 'info';
        default:
            return 'secondary';
    }
};

// Function to calculate remaining years for the project
const calculateRemainingYears = (projectSections) => {
    if (!projectSections || !Array.isArray(projectSections)) return 0;

    const currentYear = new Date().getFullYear();
    let maxYear = currentYear;

    // Loop through all sections and services to find year information
    projectSections.forEach(section => {
        if (section.services && Array.isArray(section.services)) {
            section.services.forEach(service => {
                // Check selectedYear field for the actual year value
                if (service.selectedYear && typeof service.selectedYear === 'number') {
                    if (service.selectedYear > maxYear) {
                        maxYear = service.selectedYear;
                    }
                }

                // Also check sub_services if they exist
                if (service.sub_services && Array.isArray(service.sub_services)) {
                    service.sub_services.forEach(subService => {
                        if (subService.selectedYear && typeof subService.selectedYear === 'number') {
                            if (subService.selectedYear > maxYear) {
                                maxYear = subService.selectedYear;
                            }
                        }
                    });
                }

                // Fallback: Check service_item and standard for year patterns if selectedYear is not available
                if (!service.selectedYear) {
                    const serviceItem = service.service_item || '';
                    const standard = service.standard || '';

                    // Extract years from service item or standard
                    const yearMatches = serviceItem.match(/\b(20\d{2})\b/g) || standard.match(/\b(20\d{2})\b/g);

                    if (yearMatches) {
                        yearMatches.forEach(yearStr => {
                            const year = parseInt(yearStr);
                            if (year > maxYear) {
                                maxYear = year;
                            }
                        });
                    }
                }

                // Fallback for sub_services
                if (service.sub_services && Array.isArray(service.sub_services)) {
                    service.sub_services.forEach(subService => {
                        if (!subService.selectedYear) {
                            const subServiceItem = subService.service_item || '';
                            const subStandard = subService.product_type || '';

                            const subYearMatches = subServiceItem.match(/\b(20\d{2})\b/g) || subStandard.match(/\b(20\d{2})\b/g);

                            if (subYearMatches) {
                                subYearMatches.forEach(yearStr => {
                                    const year = parseInt(yearStr);
                                    if (year > maxYear) {
                                        maxYear = year;
                                    }
                                });
                            }
                        }
                    });
                }
            });
        }
    });

    // Calculate remaining years (max year - current year + 1 to include the running year)
    const remainingYears = Math.max(1, maxYear - currentYear + 1);

    return remainingYears;
};

// Helper function to format date to dd-MM-yyyy
const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString; // Return original if invalid date
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}-${month}-${year}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString; // Return original if error
    }
};

export default DetailProject;
